// Simulateur `kubectl` 100 % côté client.
//
// Aucun vrai cluster : on maintient un état en mémoire (namespaces, deployments,
// pods, services, configmaps) et on parse les commandes `kubectl` les plus
// courantes pour produire une sortie crédible. C'est un simulateur *guidé* — il
// ne connaît que les verbes codés ici — pensé pour des exercices type CKAD où
// l'on valide l'état résultant du cluster, pas une réponse cochée.

/** Sonde (liveness / readiness / startup) — sous-ensemble CKAD. */
export interface ProbeSpec {
  kind: 'httpGet' | 'tcpSocket' | 'exec';
  path?: string; // httpGet
  port?: number | string; // httpGet / tcpSocket
  command?: string[]; // exec
  initialDelaySeconds?: number;
  periodSeconds?: number;
}

/** Montage d'un volume dans un conteneur. */
export interface VolumeMount {
  name: string;
  mountPath: string;
  readOnly?: boolean;
  subPath?: string;
}

export interface Container {
  name: string;
  image: string;
  /** ENTRYPOINT override (`command:` en YAML). */
  command?: string[];
  /** Arguments (`args:` en YAML). */
  args?: string[];
  volumeMounts?: VolumeMount[];
  livenessProbe?: ProbeSpec;
  readinessProbe?: ProbeSpec;
  startupProbe?: ProbeSpec;
}

/** Volume déclaré au niveau du pod (`spec.volumes`). */
export interface PodVolume {
  name: string;
  kind:
    | 'persistentVolumeClaim'
    | 'configMap'
    | 'secret'
    | 'emptyDir'
    | 'hostPath'
    | 'other';
  /** claimName pour un PVC, name pour configMap/secret, path pour hostPath. */
  source?: string;
}

export interface PodObj {
  name: string;
  namespace: string;
  labels: Record<string, string>;
  containers: Container[];
  volumes?: PodVolume[];
  status: 'Running' | 'Pending' | 'Completed' | 'CrashLoopBackOff';
  restarts: number;
  ownerKind: 'Deployment' | null;
  ownerName: string | null;
  node: string;
  ip: string;
  createdAt: number;
}

export interface DeploymentObj {
  name: string;
  namespace: string;
  labels: Record<string, string>;
  selector: Record<string, string>;
  replicas: number;
  containerName: string;
  image: string;
  /** Template complet du pod (conteneurs + volumes) — pour command/probes/mounts. */
  templateContainers?: Container[];
  volumes?: PodVolume[];
  /** spec.template.spec.affinity — capturé brut pour valider podAntiAffinity (CKA). */
  affinity?: Record<string, unknown>;
  /** spec.template.spec.topologySpreadConstraints — brut (CKA). */
  topologySpreadConstraints?: unknown[];
  revision: number;
  history: { revision: number; image: string }[];
  createdAt: number;
}

// ─── StatefulSet / DaemonSet (CKA) ─────────────────────────────────────────
export interface StatefulSetObj {
  name: string;
  namespace: string;
  labels: Record<string, string>;
  selector: Record<string, string>;
  replicas: number;
  containerName: string;
  image: string;
  serviceName: string | null;
  createdAt: number;
}

/** Tolérance (sous-ensemble : key + effect + operator). */
export interface TolerationSpec {
  key?: string;
  operator?: string;
  value?: string;
  effect?: string;
}

export interface DaemonSetObj {
  name: string;
  namespace: string;
  labels: Record<string, string>;
  selector: Record<string, string>;
  containerName: string;
  image: string;
  templateContainers?: Container[];
  tolerations?: TolerationSpec[];
  /** Requêtes de ressources du 1er conteneur (cpu/memory) — pour QoS CKA. */
  requests?: Record<string, string>;
  createdAt: number;
}

// ─── HorizontalPodAutoscaler (CKA) ─────────────────────────────────────────
export interface HpaObj {
  name: string;
  namespace: string;
  targetKind: string;
  targetName: string;
  minReplicas: number;
  maxReplicas: number;
  /** Utilisation CPU cible en % (metric Resource cpu Utilization). */
  cpuUtilization: number | null;
  createdAt: number;
}

export interface ServiceObj {
  name: string;
  namespace: string;
  type: 'ClusterIP' | 'NodePort';
  clusterIP: string;
  port: number;
  targetPort: number;
  selector: Record<string, string>;
  createdAt: number;
}

export interface ConfigMapObj {
  name: string;
  namespace: string;
  data: Record<string, string>;
  createdAt: number;
}

export interface SecretObj {
  name: string;
  namespace: string;
  type: string;
  data: Record<string, string>;
  createdAt: number;
}

export interface ServiceAccountObj {
  name: string;
  namespace: string;
  createdAt: number;
}

export interface NamespaceObj {
  name: string;
  createdAt: number;
}

// ─── Nœuds (CKA — administration cluster) ──────────────────────────────────
export interface NodeObj {
  name: string;
  role: 'control-plane' | 'worker';
  status: 'Ready' | 'NotReady';
  version: string;
  internalIP: string;
  taints: string[]; // ex: "node-role.kubernetes.io/control-plane:NoSchedule"
  schedulable: boolean;
  createdAt: number;
}

// ─── RBAC (CKA / CKS) ──────────────────────────────────────────────────────
export interface RoleObj {
  name: string;
  /** namespace pour Role ; '' pour ClusterRole (cluster-scoped). */
  namespace: string;
  cluster: boolean; // true = ClusterRole
  verbs: string[];
  resources: string[];
  createdAt: number;
}

export interface RoleBindingSubject {
  kind: 'ServiceAccount' | 'User' | 'Group';
  name: string;
  namespace?: string;
}

export interface RoleBindingObj {
  name: string;
  namespace: string; // '' pour ClusterRoleBinding
  cluster: boolean; // true = ClusterRoleBinding
  roleKind: 'Role' | 'ClusterRole';
  roleName: string;
  subjects: RoleBindingSubject[];
  createdAt: number;
}

// ─── NetworkPolicy (CKS / CKA) ─────────────────────────────────────────────
/** Un port autorisé dans une règle (protocol + port). */
export interface NetPolPort {
  protocol: string; // TCP / UDP
  port: number | null;
}

/** Une règle ingress/egress : cibles (to/from) + ports. */
export interface NetPolRule {
  /** matchLabels des podSelector cités dans `to`/`from` (aplati). */
  podSelectors: Record<string, string>[];
  ports: NetPolPort[];
}

export interface NetworkPolicyObj {
  name: string;
  namespace: string;
  podSelector: Record<string, string>;
  policyTypes: string[]; // Ingress / Egress
  ingress: NetPolRule[];
  egress: NetPolRule[];
  createdAt: number;
}

// ─── Stockage (CKAD) ───────────────────────────────────────────────────────
export interface StorageClassObj {
  name: string;
  provisioner: string;
  reclaimPolicy: string; // Delete | Retain
  volumeBindingMode: string; // Immediate | WaitForFirstConsumer
  isDefault: boolean;
  parameters: Record<string, string>;
  createdAt: number;
}

export interface PersistentVolumeObj {
  name: string;
  capacity: string; // ex: 1Gi
  accessModes: string[]; // ReadWriteOnce…
  reclaimPolicy: string;
  storageClassName: string | null;
  hostPath: string | null;
  status: 'Available' | 'Bound' | 'Released';
  claimRef: string | null; // "ns/pvcName"
  createdAt: number;
}

export interface PersistentVolumeClaimObj {
  name: string;
  namespace: string;
  accessModes: string[];
  storageRequest: string; // ex: 1Gi
  storageClassName: string | null;
  volumeName: string | null; // PV lié une fois Bound
  status: 'Pending' | 'Bound';
  createdAt: number;
}

export interface ClusterState {
  namespaces: NamespaceObj[];
  nodes: NodeObj[];
  deployments: DeploymentObj[];
  statefulsets: StatefulSetObj[];
  daemonsets: DaemonSetObj[];
  horizontalpodautoscalers: HpaObj[];
  pods: PodObj[];
  services: ServiceObj[];
  configmaps: ConfigMapObj[];
  secrets: SecretObj[];
  serviceaccounts: ServiceAccountObj[];
  roles: RoleObj[];
  clusterroles: RoleObj[];
  rolebindings: RoleBindingObj[];
  clusterrolebindings: RoleBindingObj[];
  networkpolicies: NetworkPolicyObj[];
  storageclasses: StorageClassObj[];
  persistentvolumes: PersistentVolumeObj[];
  persistentvolumeclaims: PersistentVolumeClaimObj[];
}

import { LinuxShell } from './shell';
import { parseYaml } from './manifest';
import { runNodeTool, type NodeToolCtx } from './nodeTools';

export interface ExecResult {
  text: string;
  isError: boolean;
}

// Alias de ressources -> forme canonique
const RESOURCE_ALIASES: Record<string, string> = {
  po: 'pods', pod: 'pods', pods: 'pods',
  deploy: 'deployments', deployment: 'deployments', deployments: 'deployments', deploy_: 'deployments',
  sts: 'statefulsets', statefulset: 'statefulsets', statefulsets: 'statefulsets',
  ds: 'daemonsets', daemonset: 'daemonsets', daemonsets: 'daemonsets',
  hpa: 'horizontalpodautoscalers', horizontalpodautoscaler: 'horizontalpodautoscalers', horizontalpodautoscalers: 'horizontalpodautoscalers',
  svc: 'services', service: 'services', services: 'services',
  cm: 'configmaps', configmap: 'configmaps', configmaps: 'configmaps',
  ns: 'namespaces', namespace: 'namespaces', namespaces: 'namespaces',
  no: 'nodes', node: 'nodes', nodes: 'nodes',
  secret: 'secrets', secrets: 'secrets',
  sa: 'serviceaccounts', serviceaccount: 'serviceaccounts', serviceaccounts: 'serviceaccounts',
  role: 'roles', roles: 'roles',
  clusterrole: 'clusterroles', clusterroles: 'clusterroles',
  rolebinding: 'rolebindings', rolebindings: 'rolebindings', rb: 'rolebindings',
  clusterrolebinding: 'clusterrolebindings', clusterrolebindings: 'clusterrolebindings',
  netpol: 'networkpolicies', networkpolicy: 'networkpolicies', networkpolicies: 'networkpolicies',
  sc: 'storageclasses', storageclass: 'storageclasses', storageclasses: 'storageclasses',
  pv: 'persistentvolumes', persistentvolume: 'persistentvolumes', persistentvolumes: 'persistentvolumes',
  pvc: 'persistentvolumeclaims', persistentvolumeclaim: 'persistentvolumeclaims', persistentvolumeclaims: 'persistentvolumeclaims',
  all: 'all',
};

/**
 * Collecte TOUTES les valeurs d'un flag répétable (`--verb=get,list` OU
 * `--verb get --verb list`) sous forme de liste aplatie. Nécessaire pour RBAC
 * où `--verb`/`--resource`/`--serviceaccount`/`--user` peuvent apparaître
 * plusieurs fois ou séparés par des virgules.
 */
function collectFlag(args: string[], name: string): string[] {
  const out: string[] = [];
  for (let i = 0; i < args.length; i++) {
    const a = args[i];
    if (a === `--${name}` && i + 1 < args.length) out.push(...args[++i].split(','));
    else if (a.startsWith(`--${name}=`)) out.push(...a.slice(name.length + 3).split(','));
  }
  return out.map((s) => s.trim()).filter(Boolean);
}

// Version Kubernetes simulée (alignée sur la doc killer.sh 1.35).
const K8S_VERSION = 'v1.35.6';
const RANDOM_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
function randSuffix(len: number): string {
  let out = '';
  for (let i = 0; i < len; i++) out += RANDOM_ALPHABET[Math.floor(Math.random() * RANDOM_ALPHABET.length)];
  return out;
}

function randIp(): string {
  return `10.244.${Math.floor(Math.random() * 4)}.${2 + Math.floor(Math.random() * 250)}`;
}

// Nœud worker aléatoire (placement des pods planifiés).
function randWorker(): string {
  return `cka-node${1 + Math.floor(Math.random() * 2)}`;
}

interface ParsedFlags {
  positional: string[];
  flags: Record<string, string | boolean>;
}

// Retire les guillemets englobants (le simulateur n'a pas de shell qui les
// dépouille) : `--labels="k=v"` → `k=v`, `'foo'` → `foo`.
function unquote(v: string): string {
  if (v.length >= 2 && ((v[0] === '"' && v.endsWith('"')) || (v[0] === "'" && v.endsWith("'")))) {
    return v.slice(1, -1);
  }
  return v;
}

// Extrait --key=val, --key val, -n val, et les booléens (--all-namespaces, -A…)
function parseArgs(tokens: string[]): ParsedFlags {
  const positional: string[] = [];
  const flags: Record<string, string | boolean> = {};
  const valueFlags = new Set(['image', 'replicas', 'port', 'target-port', 'name', 'from-literal', 'n', 'namespace', 'to-revision', 'o', 'output', 'l', 'selector', 'type', 'f', 'filename', 'dry-run', 'verb', 'resource', 'resource-name', 'role', 'clusterrole', 'serviceaccount', 'user', 'group', 'min', 'max', 'cpu-percent', 'labels']);

  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i];
    if (tok.startsWith('--')) {
      const body = tok.slice(2);
      const eq = body.indexOf('=');
      if (eq >= 0) {
        flags[body.slice(0, eq)] = unquote(body.slice(eq + 1));
      } else if (valueFlags.has(body) && i + 1 < tokens.length && !tokens[i + 1].startsWith('-')) {
        flags[body] = unquote(tokens[++i]);
      } else {
        flags[body] = true;
      }
    } else if (tok.startsWith('-') && tok.length > 1) {
      const body = tok.slice(1);
      if (body === 'A') { flags['all-namespaces'] = true; }
      else if (valueFlags.has(body) && i + 1 < tokens.length) { flags[body] = unquote(tokens[++i]); }
      else { flags[body] = true; }
    } else {
      positional.push(unquote(tok));
    }
  }
  return { positional, flags };
}

function fmtAge(createdAt: number): string {
  const s = Math.max(0, Math.floor((Date.now() - createdAt) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function table(rows: string[][]): string {
  if (rows.length === 0) return '';
  const widths: number[] = [];
  for (const row of rows) {
    row.forEach((cell, i) => {
      widths[i] = Math.max(widths[i] || 0, cell.length);
    });
  }
  return rows
    .map((row) => row.map((cell, i) => (i === row.length - 1 ? cell : cell.padEnd(widths[i] + 3))).join(''))
    .join('\n');
}

export class ClusterEngine {
  private state: ClusterState;
  private shell = new LinuxShell();
  // Services systemd simulés (kubelet…) et nœud « courant » (ssh).
  private services: Record<string, boolean> = { kubelet: true, containerd: true };
  private currentNodeName = 'cka-cp';

  private nodeToolCtx(): NodeToolCtx {
    return {
      state: this.state,
      shell: this.shell,
      services: this.services,
      currentNode: () => this.state.nodes.find((n) => n.name === this.currentNodeName)
        || this.state.nodes.find((n) => n.role === 'control-plane')
        || this.state.nodes[0],
    };
  }

  constructor() {
    this.state = {
      namespaces: [
        { name: 'default', createdAt: Date.now() },
        { name: 'kube-system', createdAt: Date.now() },
      ],
      nodes: [
        { name: 'cka-cp', role: 'control-plane', status: 'Ready', version: K8S_VERSION, internalIP: '192.168.100.11', taints: ['node-role.kubernetes.io/control-plane:NoSchedule'], schedulable: true, createdAt: Date.now() - 8 * 864e5 },
        { name: 'cka-node1', role: 'worker', status: 'Ready', version: K8S_VERSION, internalIP: '192.168.100.12', taints: [], schedulable: true, createdAt: Date.now() - 8 * 864e5 },
        { name: 'cka-node2', role: 'worker', status: 'Ready', version: K8S_VERSION, internalIP: '192.168.100.13', taints: [], schedulable: true, createdAt: Date.now() - 8 * 864e5 },
      ],
      deployments: [],
      statefulsets: [],
      daemonsets: [],
      horizontalpodautoscalers: [],
      pods: [],
      services: [],
      configmaps: [],
      secrets: [],
      serviceaccounts: [],
      roles: [],
      clusterroles: [],
      rolebindings: [],
      clusterrolebindings: [],
      networkpolicies: [],
      // StorageClass par défaut (comme minikube) : provisionnement dynamique.
      storageclasses: [
        {
          name: 'standard',
          provisioner: 'k8s.io/minikube-hostpath',
          reclaimPolicy: 'Delete',
          volumeBindingMode: 'Immediate',
          isDefault: true,
          parameters: {},
          createdAt: Date.now(),
        },
      ],
      persistentvolumes: [],
      persistentvolumeclaims: [],
    };
  }

  /** Copie profonde de l'état — les validateurs de lab s'appuient dessus. */
  snapshot(): ClusterState {
    return JSON.parse(JSON.stringify(this.state));
  }

  reset(): void {
    this.state = new ClusterEngine().snapshot();
    this.shell.reset();
    this.services = { kubelet: true, containerd: true };
    this.currentNodeName = 'cka-cp';
  }

  /** Pré-remplit un fichier dans le VFS (manifestes fournis avec un lab). */
  seedFile(path: string, content: string): void {
    this.shell.saveFile(path, content);
  }

  /**
   * Pré-remplit l'ÉTAT du cluster à partir d'un ou plusieurs manifestes YAML,
   * sans passer par le VFS ni produire de sortie. Sert aux labs dont le scénario
   * suppose des ressources déjà en place (ex. un StatefulSet à réduire, des pods
   * existants à isoler par NetworkPolicy). Silencieux : erreurs ignorées.
   */
  seed(yaml: string): void {
    let docs: Record<string, unknown>[];
    try { docs = parseYaml(yaml); } catch { return; }
    for (const doc of docs) this.applyManifest(doc, 'default', 'create');
  }

  /** Lit un fichier du VFS (pour ouvrir un éditeur vim/nano). null si absent. */
  readFile(path: string): string | null {
    return this.shell.readFile(path);
  }

  /** Noms des fichiers du répertoire courant (complétion Tab). */
  listFiles(): string[] {
    return this.shell.listNames();
  }

  private ok(text = ''): ExecResult { return { text, isError: false }; }
  private err(text: string): ExecResult { return { text, isError: true }; }

  private resolveNs(flags: Record<string, string | boolean>): string {
    return (flags['namespace'] as string) || (flags['n'] as string) || 'default';
  }

  private ensureNs(name: string): void {
    if (!this.state.namespaces.some((n) => n.name === name)) {
      this.state.namespaces.push({ name, createdAt: Date.now() });
    }
  }

  // Reconstruit les pods gérés par un deployment pour coller à son nombre de replicas.
  private reconcilePods(dep: DeploymentObj): void {
    const owned = this.state.pods.filter((p) => p.ownerName === dep.name && p.namespace === dep.namespace && p.ownerKind === 'Deployment');
    // Supprime les pods en trop
    if (owned.length > dep.replicas) {
      const toRemove = owned.slice(dep.replicas);
      this.state.pods = this.state.pods.filter((p) => !toRemove.includes(p));
    }
    // Conteneurs du template (avec command/args/probes/mounts) ; fallback au
    // conteneur unique historique si le template n'a pas été capturé.
    const templateContainers = (): Container[] =>
      dep.templateContainers && dep.templateContainers.length
        ? dep.templateContainers.map((c) => ({ ...c, image: c.name === dep.containerName ? dep.image : c.image }))
        : [{ name: dep.containerName, image: dep.image }];
    const templateVolumes = (): PodVolume[] | undefined =>
      dep.volumes && dep.volumes.length ? dep.volumes.map((v) => ({ ...v })) : undefined;

    // Ajoute les pods manquants
    for (let i = owned.length; i < dep.replicas; i++) {
      this.state.pods.push({
        name: `${dep.name}-${randSuffix(9)}-${randSuffix(5)}`,
        namespace: dep.namespace,
        labels: { ...dep.selector },
        containers: templateContainers(),
        volumes: templateVolumes(),
        status: 'Running',
        restarts: 0,
        ownerKind: 'Deployment',
        ownerName: dep.name,
        node: randWorker(),
        ip: randIp(),
        createdAt: Date.now(),
      });
    }
    // Met à jour les pods conservés (cas d'un set image / re-apply)
    this.state.pods
      .filter((p) => p.ownerName === dep.name && p.namespace === dep.namespace)
      .forEach((p) => { p.containers = templateContainers(); p.volumes = templateVolumes(); });
  }

  /** Point d'entrée : gère la redirection de sortie (> / >>) puis dispatche. */
  exec(line: string): ExecResult {
    const trimmed = line.trim();
    if (!trimmed) return this.ok('');

    // Redirection : « cmd ... > fichier » / « >> fichier ». Capture la sortie de
    // n'importe quelle commande (kubectl OU shell) et l'écrit dans le VFS.
    const m = trimmed.match(/\s(>>?)\s*(\S+)\s*$/);
    if (m) {
      const append = m[1] === '>>';
      const file = m[2];
      const res = this.dispatch(trimmed.slice(0, m.index).trim());
      if (res.isError || res.text === '__CLEAR__') return res;
      const prev = append ? (this.shell.readFile(file) ?? '') : '';
      this.shell.saveFile(file, prev + (res.text ? res.text + '\n' : ''));
      return this.ok('');
    }
    return this.dispatch(trimmed);
  }

  private dispatch(trimmed: string): ExecResult {
    const tokens = trimmed.split(/\s+/);
    const cmd = tokens[0];

    if (cmd === 'clear') return this.ok('__CLEAR__');
    if (cmd === 'help') return this.ok(this.helpText());
    // Tout ce qui n'est pas kubectl part au shell Linux (ls, cat, echo, mkdir…).
    if (cmd !== 'kubectl' && cmd !== 'k') {
      // sudo [-i] [cmd...] : exécute la suite en « root » (simulé).
      if (cmd === 'sudo') {
        const after = tokens.slice(1).filter((t, i) => !(i === 0 && (t === '-i' || t === '-s' || t === '-E')));
        if (after.length === 0) return this.ok('');
        return this.dispatch(after.join(' '));
      }
      // ssh <cible> : bascule de nœud comme à l'examen (chaque question sur son instance).
      if (cmd === 'ssh') {
        const target = tokens[1];
        if (!target) return this.err('usage: ssh <hôte>');
        const node = this.state.nodes.find((n) => n.name === target || target.endsWith(n.name) || n.name.endsWith(target));
        if (node) this.currentNodeName = node.name;
        return this.ok(`Welcome to Ubuntu 22.04.5 LTS (GNU/Linux 5.15.0-1078-aws x86_64)\n\nLast login: ${new Date().toUTCString()} from 10.0.0.1\ncandidate@${target}:~$  (session simulée — utilisez « exit » pour revenir)`);
      }
      if (cmd === 'exit' || cmd === 'logout') {
        const wasRemote = this.currentNodeName !== 'cka-cp';
        this.currentNodeName = 'cka-cp';
        return this.ok(wasRemote ? 'logout\nConnection closed.' : 'logout');
      }
      // Binaires « niveau-nœud » : crictl, kubeadm, openssl, systemctl/service, etcdctl/etcdutl.
      const nt = runNodeTool(trimmed, this.nodeToolCtx());
      if (nt) return nt;
      // Sinon → shell Linux (ls, cat, echo, grep, mkdir…).
      return this.shell.exec(trimmed);
    }

    // kubectl accepte les flags globaux (-n/--namespace, -A) AVANT le verbe :
    // `kubectl -n ns get pods`. On les extrait puis on les réinjecte dans `rest`
    // pour que les sous-commandes (qui parsent via parseArgs) les prennent.
    let idx = 1;
    const globals: string[] = [];
    while (idx < tokens.length && tokens[idx].startsWith('-')) {
      const t = tokens[idx];
      globals.push(t);
      if ((t === '-n' || t === '--namespace') && idx + 1 < tokens.length && !tokens[idx + 1].startsWith('-')) {
        globals.push(tokens[idx + 1]);
        idx++;
      }
      idx++;
    }
    const verb = tokens[idx];
    const rest = tokens.slice(idx + 1).concat(globals);

    if (!verb) {
      if (globals.includes('--help') || globals.includes('-h')) return this.ok(this.helpText());
      return this.err("You must specify a command. Tape 'kubectl help' ou 'help'.");
    }

    // `kubectl <verb> --help` ou `kubectl --help` : renvoie l'aide.
    if (verb === '--help' || verb === '-h' || rest.includes('--help') || rest.includes('-h')) {
      return this.ok(this.helpText());
    }

    switch (verb) {
      case 'get': return this.cmdGet(rest);
      case 'apply': return this.cmdApplyFile(rest, 'apply');
      case 'create': return this.cmdCreate(rest);
      case 'run': return this.cmdRun(rest);
      case 'scale': return this.cmdScale(rest);
      case 'autoscale': return this.cmdAutoscale(rest);
      case 'logs': return this.cmdLogs(rest);
      case 'delete': return this.cmdDelete(rest);
      case 'expose': return this.cmdExpose(rest);
      case 'label': return this.cmdLabel(rest);
      case 'set': return this.cmdSet(rest);
      case 'rollout': return this.cmdRollout(rest);
      case 'describe': return this.cmdDescribe(rest);
      case 'explain': return this.cmdExplain(rest);
      case 'config': return this.ok('Current context: minikube (simulé)');
      case 'version': return this.ok(`Client Version: ${K8S_VERSION}\nKustomize Version: v5.7.1\nServer Version: ${K8S_VERSION}`);
      case 'help': return this.ok(this.helpText());
      default:
        return this.err(`error: unknown command "${verb}". Verbes supportés : get, create, run, scale, autoscale, delete, expose, label, set, rollout, describe, explain.`);
    }
  }

  // Doc « façon kubectl explain » pour les ressources courantes.
  private static EXPLAIN: Record<string, string> = {
    pod: [
      'KIND:     Pod',
      'VERSION:  v1',
      '',
      'DESCRIPTION:',
      '     Pod is a collection of containers that can run on a host.',
      '',
      'FIELDS:',
      '   apiVersion\t<string>',
      '   kind\t<string>',
      '   metadata\t<Object>',
      '   spec\t<Object>  Specification of the desired behavior of the pod.',
      '   status\t<Object>  Most recently observed status of the pod.',
    ].join('\n'),
    deployment: [
      'KIND:     Deployment',
      'VERSION:  apps/v1',
      '',
      'DESCRIPTION:',
      '     Deployment enables declarative updates for Pods and ReplicaSets.',
      '',
      'FIELDS:',
      '   apiVersion\t<string>',
      '   kind\t<string>',
      '   metadata\t<Object>',
      '   spec\t<Object>  spec.replicas, spec.selector, spec.template',
      '   status\t<Object>',
    ].join('\n'),
    service: [
      'KIND:     Service',
      'VERSION:  v1',
      '',
      'DESCRIPTION:',
      '     Service is a named abstraction of software service (e.g. via a proxy).',
      '',
      'FIELDS:',
      '   spec.selector\t<map[string]string>  Route traffic to Pods with these labels.',
      '   spec.ports\t<[]Object>  The list of ports exposed by this service.',
      '   spec.type\t<string>  ClusterIP, NodePort, LoadBalancer, ExternalName.',
    ].join('\n'),
    configmap: [
      'KIND:     ConfigMap',
      'VERSION:  v1',
      '',
      'DESCRIPTION:',
      '     ConfigMap holds configuration data for pods to consume.',
      '',
      'FIELDS:',
      '   data\t<map[string]string>  Configuration key/value pairs.',
    ].join('\n'),
    secret: [
      'KIND:     Secret',
      'VERSION:  v1',
      '',
      'DESCRIPTION:',
      '     Secret holds secret data of a certain type.',
      '',
      'FIELDS:',
      '   type\t<string>  Opaque, kubernetes.io/tls, kubernetes.io/dockerconfigjson…',
      '   data\t<map[string]string>  base64-encoded secret values.',
    ].join('\n'),
    namespace: [
      'KIND:     Namespace',
      'VERSION:  v1',
      '',
      'DESCRIPTION:',
      '     Namespace provides a scope for names within a cluster.',
    ].join('\n'),
    storageclass: [
      'KIND:     StorageClass',
      'VERSION:  storage.k8s.io/v1',
      '',
      'DESCRIPTION:',
      '     StorageClass describes the parameters for a class of storage for',
      '     dynamic provisioning of PersistentVolumes.',
      '',
      'FIELDS:',
      '   provisioner\t<string>  Volume plugin used for provisioning PVs.',
      '   reclaimPolicy\t<string>  Delete (default) or Retain.',
      '   volumeBindingMode\t<string>  Immediate or WaitForFirstConsumer.',
      '   parameters\t<map[string]string>  Provisioner-specific parameters.',
    ].join('\n'),
    persistentvolume: [
      'KIND:     PersistentVolume',
      'VERSION:  v1',
      '',
      'DESCRIPTION:',
      '     PersistentVolume (PV) is a piece of storage in the cluster.',
      '',
      'FIELDS:',
      '   spec.capacity\t<map[string]Quantity>  e.g. storage: 1Gi',
      '   spec.accessModes\t<[]string>  ReadWriteOnce, ReadOnlyMany, ReadWriteMany.',
      '   spec.persistentVolumeReclaimPolicy\t<string>  Retain, Delete, Recycle.',
      '   spec.storageClassName\t<string>  Name of the StorageClass.',
      '   spec.hostPath\t<Object>  HostPath source (path).',
    ].join('\n'),
    persistentvolumeclaim: [
      'KIND:     PersistentVolumeClaim',
      'VERSION:  v1',
      '',
      'DESCRIPTION:',
      '     PersistentVolumeClaim (PVC) is a request for storage by a user.',
      '',
      'FIELDS:',
      '   spec.accessModes\t<[]string>  Desired access modes.',
      '   spec.resources.requests.storage\t<Quantity>  e.g. 1Gi',
      '   spec.storageClassName\t<string>  StorageClass to provision from.',
      '   status.phase\t<string>  Pending or Bound.',
    ].join('\n'),
  };

  // Canonique (pluriel) -> clé EXPLAIN (singulier) pour les noms composés dont
  // la naïve suppression du « s » final ne suffit pas.
  private static EXPLAIN_SINGULAR: Record<string, string> = {
    storageclasses: 'storageclass',
    persistentvolumes: 'persistentvolume',
    persistentvolumeclaims: 'persistentvolumeclaim',
  };

  private cmdExplain(args: string[]): ExecResult {
    const raw = (args[0] || '').toLowerCase();
    if (!raw) return this.err('You must specify the type of resource to explain. e.g. kubectl explain pods');
    // Accepte le pluriel/alias et un éventuel champ (pod.spec) : on prend la racine.
    const canonical = (RESOURCE_ALIASES[raw] || raw).split('.')[0];
    const root = ClusterEngine.EXPLAIN_SINGULAR[canonical] || canonical.replace(/s$/, '');
    const doc = ClusterEngine.EXPLAIN[root];
    if (!doc) return this.err(`error: no explain documentation for "${raw}" in this simulator.`);
    return this.ok(doc);
  }

  private splitTypeName(arg: string): { type: string; name: string | null } {
    if (arg.includes('/')) {
      const [t, n] = arg.split('/');
      return { type: RESOURCE_ALIASES[t] || t, name: n };
    }
    return { type: RESOURCE_ALIASES[arg] || arg, name: null };
  }

  private wantsYaml(flags: Record<string, string | boolean>): boolean {
    const o = (flags['o'] ?? flags['output']) as string | boolean | undefined;
    return o === 'yaml' || o === 'yml';
  }

  private isDryRun(flags: Record<string, string | boolean>): boolean {
    const d = flags['dry-run'];
    return d === 'client' || d === 'server' || d === true;
  }

  // --- apply -f / create -f : crée les ressources décrites dans un manifeste ---

  private cmdApplyFile(args: string[], verb: 'apply' | 'create'): ExecResult {
    const { flags } = parseArgs(args);
    const file = (flags['f'] ?? flags['filename']) as string | undefined;
    if (!file) return this.err(`error: must specify -f <file> for ${verb}.`);
    const content = this.shell.readFile(file);
    if (content === null) return this.err(`error: the path "${file}": no such file or directory`);
    let docs;
    try {
      docs = parseYaml(content);
    } catch {
      return this.err(`error: unable to parse "${file}": invalid YAML`);
    }
    if (docs.length === 0) return this.err(`error: no manifests found in "${file}".`);
    const defaultNs = this.resolveNs(flags);
    const out: string[] = [];
    for (const doc of docs) {
      const r = this.applyManifest(doc, defaultNs, verb);
      if (r.isError) return r;
      out.push(r.text);
    }
    return this.ok(out.join('\n'));
  }

  private str(v: unknown): string { return v == null ? '' : String(v); }

  /** Normalise une liste YAML de chaînes (command/args). */
  private strList(v: unknown): string[] | undefined {
    if (!Array.isArray(v)) return undefined;
    return v.map((x) => this.str(x));
  }

  private num(v: unknown): number | undefined {
    return typeof v === 'number' ? v : undefined;
  }

  /** Parse une sonde (livenessProbe/readinessProbe/startupProbe). */
  private parseProbe(v: unknown): ProbeSpec | undefined {
    if (!v || typeof v !== 'object') return undefined;
    const o = v as Record<string, unknown>;
    const initialDelaySeconds = this.num(o.initialDelaySeconds);
    const periodSeconds = this.num(o.periodSeconds);
    if (o.httpGet && typeof o.httpGet === 'object') {
      const h = o.httpGet as Record<string, unknown>;
      return {
        kind: 'httpGet',
        path: this.str(h.path) || '/',
        port: typeof h.port === 'number' ? h.port : this.str(h.port),
        initialDelaySeconds, periodSeconds,
      };
    }
    if (o.tcpSocket && typeof o.tcpSocket === 'object') {
      const t = o.tcpSocket as Record<string, unknown>;
      return {
        kind: 'tcpSocket',
        port: typeof t.port === 'number' ? t.port : this.str(t.port),
        initialDelaySeconds, periodSeconds,
      };
    }
    if (o.exec && typeof o.exec === 'object') {
      const e = o.exec as Record<string, unknown>;
      return {
        kind: 'exec',
        command: this.strList(e.command) || [],
        initialDelaySeconds, periodSeconds,
      };
    }
    return undefined;
  }

  /** Parse la liste `spec.containers` d'un pod/template en `Container[]`. */
  private parseContainers(list: unknown): Container[] {
    if (!Array.isArray(list)) return [];
    return (list as Record<string, unknown>[]).map((c) => {
      const mounts = Array.isArray(c.volumeMounts)
        ? (c.volumeMounts as Record<string, unknown>[]).map((m) => ({
            name: this.str(m.name),
            mountPath: this.str(m.mountPath),
            readOnly: m.readOnly === true || undefined,
            subPath: m.subPath ? this.str(m.subPath) : undefined,
          }))
        : undefined;
      const container: Container = { name: this.str(c.name), image: this.str(c.image) };
      const command = this.strList(c.command);
      const args = this.strList(c.args);
      if (command) container.command = command;
      if (args) container.args = args;
      if (mounts && mounts.length) container.volumeMounts = mounts;
      const live = this.parseProbe(c.livenessProbe);
      const ready = this.parseProbe(c.readinessProbe);
      const startup = this.parseProbe(c.startupProbe);
      if (live) container.livenessProbe = live;
      if (ready) container.readinessProbe = ready;
      if (startup) container.startupProbe = startup;
      return container;
    });
  }

  /** Parse la liste `spec.volumes` d'un pod/template en `PodVolume[]`. */
  private parseVolumes(list: unknown): PodVolume[] {
    if (!Array.isArray(list)) return [];
    return (list as Record<string, unknown>[]).map((v) => {
      const name = this.str(v.name);
      if (v.persistentVolumeClaim && typeof v.persistentVolumeClaim === 'object') {
        return { name, kind: 'persistentVolumeClaim', source: this.str((v.persistentVolumeClaim as Record<string, unknown>).claimName) };
      }
      if (v.configMap && typeof v.configMap === 'object') {
        return { name, kind: 'configMap', source: this.str((v.configMap as Record<string, unknown>).name) };
      }
      if (v.secret && typeof v.secret === 'object') {
        return { name, kind: 'secret', source: this.str((v.secret as Record<string, unknown>).secretName) };
      }
      if ('emptyDir' in v) return { name, kind: 'emptyDir' };
      if (v.hostPath && typeof v.hostPath === 'object') {
        return { name, kind: 'hostPath', source: this.str((v.hostPath as Record<string, unknown>).path) };
      }
      return { name, kind: 'other' };
    });
  }

  /** Parse `spec.tolerations` (sous-ensemble key/operator/value/effect). */
  private parseTolerations(list: unknown): TolerationSpec[] | undefined {
    if (!Array.isArray(list)) return undefined;
    const out = (list as Record<string, unknown>[]).map((t) => ({
      key: t.key != null ? this.str(t.key) : undefined,
      operator: t.operator != null ? this.str(t.operator) : undefined,
      value: t.value != null ? this.str(t.value) : undefined,
      effect: t.effect != null ? this.str(t.effect) : undefined,
    }));
    return out.length ? out : undefined;
  }

  /** Extrait `resources.requests` (cpu/memory) du 1er conteneur d'un template. */
  private parseRequests(container: Record<string, unknown> | undefined): Record<string, string> | undefined {
    if (!container) return undefined;
    const resources = (container.resources as Record<string, unknown>) || {};
    const requests = (resources.requests as Record<string, unknown>) || {};
    const out: Record<string, string> = {};
    for (const k of Object.keys(requests)) out[k] = this.str(requests[k]);
    return Object.keys(out).length ? out : undefined;
  }

  /** Parse une liste de règles ingress/egress d'une NetworkPolicy. */
  private parseNetPolRules(list: unknown, dir: 'from' | 'to'): NetPolRule[] {
    if (!Array.isArray(list)) return [];
    return (list as Record<string, unknown>[]).map((rule) => {
      const peers = (rule[dir] as Record<string, unknown>[]) || [];
      const podSelectors: Record<string, string>[] = [];
      for (const peer of peers) {
        const ps = (peer.podSelector as Record<string, unknown>)?.matchLabels as Record<string, string> | undefined;
        if (ps) podSelectors.push(ps);
      }
      const portsRaw = (rule.ports as Record<string, unknown>[]) || [];
      const ports: NetPolPort[] = portsRaw.map((p) => ({
        protocol: this.str(p.protocol) || 'TCP',
        port: typeof p.port === 'number' ? p.port : (p.port != null ? parseInt(this.str(p.port), 10) || null : null),
      }));
      return { podSelectors, ports };
    });
  }

  /** (Re)crée les pods ordinaux d'un StatefulSet (name-0..name-(replicas-1)). */
  private reconcileStsPods(sts: StatefulSetObj): void {
    // Purge les pods gérés par ce sts puis recrée les ordinaux.
    this.state.pods = this.state.pods.filter((p) => !(p.namespace === sts.namespace && p.ownerName === sts.name && p.labels['app.kubernetes.io/managed-by'] === 'statefulset-sim'));
    for (let i = 0; i < sts.replicas; i++) {
      this.state.pods.push({
        name: `${sts.name}-${i}`, namespace: sts.namespace,
        labels: { ...sts.selector, 'statefulset.kubernetes.io/pod-name': `${sts.name}-${i}`, 'app.kubernetes.io/managed-by': 'statefulset-sim' },
        containers: [{ name: sts.containerName, image: sts.image }],
        status: 'Running', restarts: 0, ownerKind: null, ownerName: sts.name,
        node: `cka-node${1 + (i % 2)}`, ip: randIp(), createdAt: Date.now(),
      });
    }
  }

  /** (Re)crée un pod de DaemonSet par nœud (2 workers + 1 control-plane). */
  private reconcileDsPods(ds: DaemonSetObj): void {
    this.state.pods = this.state.pods.filter((p) => !(p.namespace === ds.namespace && p.ownerName === ds.name && p.labels['app.kubernetes.io/managed-by'] === 'daemonset-sim'));
    const nodes = ['cka-node1', 'cka-node2', 'cka-cp'];
    for (const node of nodes) {
      this.state.pods.push({
        name: `${ds.name}-${randSuffix(5)}`, namespace: ds.namespace,
        labels: { ...ds.selector, 'app.kubernetes.io/managed-by': 'daemonset-sim' },
        containers: (ds.templateContainers && ds.templateContainers.length) ? ds.templateContainers.map((c) => ({ ...c })) : [{ name: ds.containerName, image: ds.image }],
        status: 'Running', restarts: 0, ownerKind: null, ownerName: ds.name,
        node, ip: randIp(), createdAt: Date.now(),
      });
    }
  }

  /**
   * Tente de lier un PVC (Pending → Bound). Priorité : un PV Available compatible
   * (même storageClass + capacité suffisante) ; sinon provisionnement dynamique
   * si une StorageClass existe. Un SC en WaitForFirstConsumer ne lie qu'au
   * montage par un pod (paramètre `byPod`).
   */
  private bindPvc(pvc: PersistentVolumeClaimObj, byPod = false): void {
    if (pvc.status === 'Bound') return;
    const reqGi = this.parseGi(pvc.storageRequest);

    // 1) PV statique compatible ?
    const pv = this.state.persistentvolumes.find(
      (p) =>
        p.status === 'Available' &&
        (p.storageClassName || null) === (pvc.storageClassName || null) &&
        this.parseGi(p.capacity) >= reqGi,
    );
    if (pv) {
      pv.status = 'Bound';
      pv.claimRef = `${pvc.namespace}/${pvc.name}`;
      pvc.status = 'Bound';
      pvc.volumeName = pv.name;
      return;
    }

    // 2) Provisionnement dynamique via StorageClass.
    const scName =
      pvc.storageClassName ??
      this.state.storageclasses.find((s) => s.isDefault)?.name ??
      null;
    const sc = this.state.storageclasses.find((s) => s.name === scName);
    if (!sc) return; // aucune SC → reste Pending (PV statique requis)
    if (sc.volumeBindingMode === 'WaitForFirstConsumer' && !byPod) return;

    // Crée un PV dynamique et lie.
    const pvName = `pvc-${randSuffix(8)}`;
    this.state.persistentvolumes.push({
      name: pvName,
      capacity: pvc.storageRequest,
      accessModes: [...pvc.accessModes],
      reclaimPolicy: sc.reclaimPolicy,
      storageClassName: sc.name,
      hostPath: `/tmp/hostpath-provisioner/${pvc.namespace}/${pvc.name}`,
      status: 'Bound',
      claimRef: `${pvc.namespace}/${pvc.name}`,
      createdAt: Date.now(),
    });
    pvc.status = 'Bound';
    pvc.volumeName = pvName;
  }

  /** Lie les PVC référencés par les volumes d'un pod (déclencheur "premier consommateur"). */
  private bindPodClaims(volumes: PodVolume[], ns: string): void {
    for (const v of volumes) {
      if (v.kind !== 'persistentVolumeClaim' || !v.source) continue;
      const pvc = this.state.persistentvolumeclaims.find(
        (c) => c.name === v.source && c.namespace === ns,
      );
      if (pvc && pvc.status === 'Pending') this.bindPvc(pvc, true);
    }
  }

  /** Abréviations kubectl des access modes (RWO/ROX/RWX/RWOP). */
  private shortAccessModes(modes: string[]): string {
    const map: Record<string, string> = {
      ReadWriteOnce: 'RWO',
      ReadOnlyMany: 'ROX',
      ReadWriteMany: 'RWX',
      ReadWriteOncePod: 'RWOP',
    };
    return modes.map((m) => map[m] || m).join(',');
  }

  /** Convertit une quantité de stockage (ex "1Gi", "500Mi") en Gi (approx). */
  private parseGi(q: string): number {
    const m = /^(\d+(?:\.\d+)?)\s*(Gi|Mi|Ti|G|M|T)?$/.exec(q.trim());
    if (!m) return 0;
    const n = parseFloat(m[1]);
    switch (m[2]) {
      case 'Ti': case 'T': return n * 1024;
      case 'Mi': case 'M': return n / 1024;
      case 'Gi': case 'G': default: return n;
    }
  }

  private applyManifest(doc: Record<string, unknown>, defaultNs: string, verb: 'apply' | 'create'): ExecResult {
    const kind = this.str(doc.kind);
    const meta = (doc.metadata as Record<string, unknown>) || {};
    const spec = (doc.spec as Record<string, unknown>) || {};
    const name = this.str(meta.name);
    const ns = this.str(meta.namespace) || defaultNs;
    if (!kind || !name) return this.err('error: manifest is missing kind or metadata.name');
    this.ensureNs(ns);
    const labels = (meta.labels as Record<string, string>) || {};
    const exists = (arr: { name: string; namespace: string }[]) => arr.some((x) => x.name === name && x.namespace === ns);
    const verbNoun = verb === 'apply' ? 'configured' : 'created';

    switch (kind) {
      case 'Namespace':
        this.ensureNs(name);
        return this.ok(`namespace/${name} ${verbNoun}`);
      case 'Pod': {
        const containers = this.parseContainers(spec.containers);
        if (containers.length === 0) return this.err(`error: Pod "${name}" has no containers`);
        if (exists(this.state.pods) && verb === 'create') return this.err(`Error from server (AlreadyExists): pods "${name}" already exists`);
        const volumes = this.parseVolumes(spec.volumes);
        this.bindPodClaims(volumes, ns);
        this.state.pods = this.state.pods.filter((p) => !(p.name === name && p.namespace === ns));
        this.state.pods.push({
          name, namespace: ns, labels: Object.keys(labels).length ? labels : { run: name },
          containers, volumes: volumes.length ? volumes : undefined,
          status: 'Running', restarts: 0, ownerKind: null, ownerName: null,
          node: randWorker(), ip: randIp(), createdAt: Date.now(),
        });
        return this.ok(`pod/${name} ${verbNoun}`);
      }
      case 'Deployment': {
        const template = (spec.template as Record<string, unknown>) || {};
        const tspec = (template.spec as Record<string, unknown>) || {};
        const templateContainers = this.parseContainers(tspec.containers);
        const c = templateContainers[0];
        if (!c) return this.err(`error: Deployment "${name}" has no containers`);
        const replicas = typeof spec.replicas === 'number' ? spec.replicas : 1;
        const selector = ((spec.selector as Record<string, unknown>)?.matchLabels as Record<string, string>) || { app: name };
        const volumes = this.parseVolumes(tspec.volumes);
        this.bindPodClaims(volumes, ns);
        this.state.deployments = this.state.deployments.filter((d) => !(d.name === name && d.namespace === ns));
        this.state.pods = this.state.pods.filter((p) => !(p.ownerName === name && p.namespace === ns));
        const dep: DeploymentObj = {
          name, namespace: ns, labels: Object.keys(labels).length ? labels : { app: name },
          selector, replicas, containerName: c.name || name, image: c.image,
          templateContainers, volumes: volumes.length ? volumes : undefined,
          affinity: (tspec.affinity as Record<string, unknown>) || undefined,
          topologySpreadConstraints: Array.isArray(tspec.topologySpreadConstraints)
            ? (tspec.topologySpreadConstraints as unknown[])
            : undefined,
          revision: 1, history: [{ revision: 1, image: c.image }], createdAt: Date.now(),
        };
        this.state.deployments.push(dep);
        this.reconcilePods(dep);
        return this.ok(`deployment.apps/${name} ${verbNoun}`);
      }
      case 'StatefulSet': {
        const template = (spec.template as Record<string, unknown>) || {};
        const tspec = (template.spec as Record<string, unknown>) || {};
        const c = this.parseContainers(tspec.containers)[0];
        if (!c) return this.err(`error: StatefulSet "${name}" has no containers`);
        const replicas = typeof spec.replicas === 'number' ? spec.replicas : 1;
        const selector = ((spec.selector as Record<string, unknown>)?.matchLabels as Record<string, string>) || { app: name };
        this.state.statefulsets = this.state.statefulsets.filter((x) => !(x.name === name && x.namespace === ns));
        this.state.pods = this.state.pods.filter((p) => !(p.ownerName === name && p.namespace === ns));
        const sts: StatefulSetObj = {
          name, namespace: ns, labels: Object.keys(labels).length ? labels : { app: name },
          selector, replicas, containerName: c.name || name, image: c.image,
          serviceName: spec.serviceName ? this.str(spec.serviceName) : null, createdAt: Date.now(),
        };
        this.state.statefulsets.push(sts);
        this.reconcileStsPods(sts);
        return this.ok(`statefulset.apps/${name} ${verbNoun}`);
      }
      case 'DaemonSet': {
        const template = (spec.template as Record<string, unknown>) || {};
        const tspec = (template.spec as Record<string, unknown>) || {};
        const templateContainers = this.parseContainers(tspec.containers);
        const c = templateContainers[0];
        if (!c) return this.err(`error: DaemonSet "${name}" has no containers`);
        const selector = ((spec.selector as Record<string, unknown>)?.matchLabels as Record<string, string>) || { app: name };
        this.state.daemonsets = this.state.daemonsets.filter((x) => !(x.name === name && x.namespace === ns));
        this.state.pods = this.state.pods.filter((p) => !(p.ownerName === name && p.namespace === ns));
        const ds: DaemonSetObj = {
          name, namespace: ns, labels: Object.keys(labels).length ? labels : { app: name },
          selector, containerName: c.name || name, image: c.image, templateContainers,
          tolerations: this.parseTolerations(tspec.tolerations),
          requests: this.parseRequests((tspec.containers as Record<string, unknown>[])?.[0]),
          createdAt: Date.now(),
        };
        this.state.daemonsets.push(ds);
        this.reconcileDsPods(ds);
        return this.ok(`daemonset.apps/${name} ${verbNoun}`);
      }
      case 'HorizontalPodAutoscaler': {
        const ref = (spec.scaleTargetRef as Record<string, unknown>) || {};
        const metrics = (spec.metrics as Record<string, unknown>[]) || [];
        let cpu: number | null = null;
        for (const m of metrics) {
          const res = (m.resource as Record<string, unknown>) || {};
          if (this.str(res.name) === 'cpu') {
            const target = (res.target as Record<string, unknown>) || {};
            cpu = this.num(target.averageUtilization) ?? null;
          }
        }
        const hpa: HpaObj = {
          name, namespace: ns,
          targetKind: this.str(ref.kind) || 'Deployment',
          targetName: this.str(ref.name),
          minReplicas: this.num(spec.minReplicas) ?? 1,
          maxReplicas: this.num(spec.maxReplicas) ?? 1,
          cpuUtilization: cpu, createdAt: Date.now(),
        };
        const idx = this.state.horizontalpodautoscalers.findIndex((x) => x.name === name && x.namespace === ns);
        if (idx >= 0) this.state.horizontalpodautoscalers[idx] = hpa; else this.state.horizontalpodautoscalers.push(hpa);
        return this.ok(`horizontalpodautoscaler.autoscaling/${name} ${verbNoun}`);
      }
      case 'Service': {
        const ports = ((spec.ports as Record<string, unknown>[]) || [])[0] || {};
        const port = typeof ports.port === 'number' ? ports.port : 80;
        const targetPort = typeof ports.targetPort === 'number' ? ports.targetPort : port;
        const selector = (spec.selector as Record<string, string>) || {};
        this.state.services = this.state.services.filter((s) => !(s.name === name && s.namespace === ns));
        this.state.services.push({
          name, namespace: ns, type: (this.str(spec.type) as 'ClusterIP' | 'NodePort') || 'ClusterIP',
          clusterIP: `10.96.${Math.floor(Math.random() * 250)}.${Math.floor(Math.random() * 250)}`,
          port, targetPort, selector, createdAt: Date.now(),
        });
        return this.ok(`service/${name} ${verbNoun}`);
      }
      case 'ConfigMap': {
        this.state.configmaps = this.state.configmaps.filter((c) => !(c.name === name && c.namespace === ns));
        this.state.configmaps.push({ name, namespace: ns, data: (doc.data as Record<string, string>) || {}, createdAt: Date.now() });
        return this.ok(`configmap/${name} ${verbNoun}`);
      }
      case 'Secret': {
        this.state.secrets = this.state.secrets.filter((c) => !(c.name === name && c.namespace === ns));
        this.state.secrets.push({ name, namespace: ns, type: this.str(doc.type) || 'Opaque', data: (doc.data as Record<string, string>) || {}, createdAt: Date.now() });
        return this.ok(`secret/${name} ${verbNoun}`);
      }
      case 'ServiceAccount': {
        this.state.serviceaccounts = this.state.serviceaccounts.filter((c) => !(c.name === name && c.namespace === ns));
        this.state.serviceaccounts.push({ name, namespace: ns, createdAt: Date.now() });
        return this.ok(`serviceaccount/${name} ${verbNoun}`);
      }
      case 'Role':
      case 'ClusterRole': {
        const isCluster = kind === 'ClusterRole';
        const rules = (doc.rules as Record<string, unknown>[]) || [];
        const verbs = [...new Set(rules.flatMap((r) => (r.verbs as string[]) || []))];
        const resources = [...new Set(rules.flatMap((r) => (r.resources as string[]) || []))];
        const role: RoleObj = { name, namespace: isCluster ? '' : ns, cluster: isCluster, verbs, resources, createdAt: Date.now() };
        const arr = isCluster ? this.state.clusterroles : this.state.roles;
        const idx = arr.findIndex((x) => x.name === name && x.namespace === role.namespace);
        if (idx >= 0) arr[idx] = role; else arr.push(role);
        return this.ok(`${isCluster ? 'clusterrole' : 'role'}.rbac.authorization.k8s.io/${name} ${verbNoun}`);
      }
      case 'RoleBinding':
      case 'ClusterRoleBinding': {
        const isCluster = kind === 'ClusterRoleBinding';
        const roleRef = (doc.roleRef as Record<string, unknown>) || {};
        const subjectsRaw = (doc.subjects as Record<string, unknown>[]) || [];
        const subjects: RoleBindingSubject[] = subjectsRaw.map((su) => ({
          kind: (this.str(su.kind) as RoleBindingSubject['kind']) || 'ServiceAccount',
          name: this.str(su.name),
          namespace: su.namespace ? this.str(su.namespace) : undefined,
        }));
        const binding: RoleBindingObj = {
          name, namespace: isCluster ? '' : ns, cluster: isCluster,
          roleKind: (this.str(roleRef.kind) as RoleBindingObj['roleKind']) || 'Role',
          roleName: this.str(roleRef.name), subjects, createdAt: Date.now(),
        };
        const arr = isCluster ? this.state.clusterrolebindings : this.state.rolebindings;
        const idx = arr.findIndex((x) => x.name === name && x.namespace === binding.namespace);
        if (idx >= 0) arr[idx] = binding; else arr.push(binding);
        return this.ok(`${isCluster ? 'clusterrolebinding' : 'rolebinding'}.rbac.authorization.k8s.io/${name} ${verbNoun}`);
      }
      case 'NetworkPolicy': {
        const podSelector = ((spec.podSelector as Record<string, unknown>)?.matchLabels as Record<string, string>) || {};
        const policyTypes = ((spec.policyTypes as string[]) || []).map(String);
        const ingress = this.parseNetPolRules(spec.ingress, 'from');
        const egress = this.parseNetPolRules(spec.egress, 'to');
        const np: NetworkPolicyObj = { name, namespace: ns, podSelector, policyTypes, ingress, egress, createdAt: Date.now() };
        const idx = this.state.networkpolicies.findIndex((x) => x.name === name && x.namespace === ns);
        if (idx >= 0) this.state.networkpolicies[idx] = np; else this.state.networkpolicies.push(np);
        return this.ok(`networkpolicy.networking.k8s.io/${name} ${verbNoun}`);
      }
      case 'StorageClass': {
        // Cluster-scoped : pas de namespace.
        const annotations = (meta.annotations as Record<string, string>) || {};
        const isDefault =
          annotations['storageclass.kubernetes.io/is-default-class'] === 'true';
        const sc: StorageClassObj = {
          name,
          provisioner: this.str(doc.provisioner) || 'kubernetes.io/no-provisioner',
          reclaimPolicy: this.str(doc.reclaimPolicy) || 'Delete',
          volumeBindingMode: this.str(doc.volumeBindingMode) || 'Immediate',
          isDefault,
          parameters: (doc.parameters as Record<string, string>) || {},
          createdAt: Date.now(),
        };
        // Une seule SC par défaut.
        if (isDefault) this.state.storageclasses.forEach((s) => (s.isDefault = false));
        const idx = this.state.storageclasses.findIndex((x) => x.name === name);
        if (idx >= 0) this.state.storageclasses[idx] = sc; else this.state.storageclasses.push(sc);
        return this.ok(`storageclass.storage.k8s.io/${name} ${verbNoun}`);
      }
      case 'PersistentVolume': {
        const capacity = this.str((spec.capacity as Record<string, unknown>)?.storage) || '1Gi';
        const pv: PersistentVolumeObj = {
          name,
          capacity,
          accessModes: (spec.accessModes as string[] | undefined)?.map(String) || ['ReadWriteOnce'],
          reclaimPolicy: this.str(spec.persistentVolumeReclaimPolicy) || 'Retain',
          storageClassName: spec.storageClassName ? this.str(spec.storageClassName) : null,
          hostPath: (spec.hostPath as Record<string, unknown>)?.path ? this.str((spec.hostPath as Record<string, unknown>).path) : null,
          status: 'Available',
          claimRef: null,
          createdAt: Date.now(),
        };
        const idx = this.state.persistentvolumes.findIndex((x) => x.name === name);
        if (idx >= 0) this.state.persistentvolumes[idx] = pv; else this.state.persistentvolumes.push(pv);
        // Un PVC en attente peut désormais se lier à ce PV.
        this.state.persistentvolumeclaims
          .filter((c) => c.status === 'Pending')
          .forEach((c) => this.bindPvc(c));
        return this.ok(`persistentvolume/${name} ${verbNoun}`);
      }
      case 'PersistentVolumeClaim': {
        const resources = (spec.resources as Record<string, unknown>) || {};
        const requests = (resources.requests as Record<string, unknown>) || {};
        const pvc: PersistentVolumeClaimObj = {
          name, namespace: ns,
          accessModes: (spec.accessModes as string[] | undefined)?.map(String) || ['ReadWriteOnce'],
          storageRequest: this.str(requests.storage) || '1Gi',
          storageClassName: spec.storageClassName != null ? this.str(spec.storageClassName) : null,
          volumeName: null,
          status: 'Pending',
          createdAt: Date.now(),
        };
        const idx = this.state.persistentvolumeclaims.findIndex((x) => x.name === name && x.namespace === ns);
        if (idx >= 0) this.state.persistentvolumeclaims[idx] = pvc; else this.state.persistentvolumeclaims.push(pvc);
        this.bindPvc(pvc); // Immediate / PV statique → Bound tout de suite
        return this.ok(`persistentvolumeclaim/${name} ${verbNoun}`);
      }
      default:
        return this.err(`error: kind "${kind}" is not supported by this simulator.`);
    }
  }

  // Génère un manifeste YAML (pour --dry-run=client -o yaml et get -o yaml).
  private deploymentYaml(name: string, image: string, replicas: number): string {
    return [
      'apiVersion: apps/v1',
      'kind: Deployment',
      'metadata:',
      `  name: ${name}`,
      `  labels:`,
      `    app: ${name}`,
      'spec:',
      `  replicas: ${replicas}`,
      '  selector:',
      '    matchLabels:',
      `      app: ${name}`,
      '  template:',
      '    metadata:',
      '      labels:',
      `        app: ${name}`,
      '    spec:',
      '      containers:',
      `      - name: ${name}`,
      `        image: ${image}`,
    ].join('\n');
  }

  private podYaml(name: string, image: string): string {
    return [
      'apiVersion: v1',
      'kind: Pod',
      'metadata:',
      `  name: ${name}`,
      '  labels:',
      `    run: ${name}`,
      'spec:',
      '  containers:',
      `  - name: ${name}`,
      `    image: ${image}`,
    ].join('\n');
  }

  private cmdGet(args: string[]): ExecResult {
    const { positional, flags } = parseArgs(args);
    if (positional.length === 0) return this.err('error: you must specify the type of resource to get.');

    const { type, name } = this.splitTypeName(positional[0]);
    const explicitName = name || positional[1] || null;
    const allNs = flags['all-namespaces'] === true;
    const ns = this.resolveNs(flags);

    // get <type>/<name> -o yaml : renvoie le manifeste de la ressource.
    if (this.wantsYaml(flags) && explicitName) {
      if (type === 'deployments') {
        const d = this.state.deployments.find((x) => x.name === explicitName && x.namespace === ns);
        if (!d) return this.err(`Error from server (NotFound): deployments.apps "${explicitName}" not found`);
        return this.ok(this.deploymentYaml(d.name, d.image, d.replicas));
      }
      if (type === 'pods') {
        const p = this.state.pods.find((x) => x.name === explicitName && x.namespace === ns);
        if (!p) return this.err(`Error from server (NotFound): pods "${explicitName}" not found`);
        return this.ok(this.podYaml(p.name, p.containers[0]?.image || ''));
      }
    }

    if (type === 'all') {
      const parts: string[] = [];
      const d = this.cmdGet(['deployments', ...(allNs ? ['-A'] : ['-n', ns])]);
      const p = this.cmdGet(['pods', ...(allNs ? ['-A'] : ['-n', ns])]);
      const s = this.cmdGet(['services', ...(allNs ? ['-A'] : ['-n', ns])]);
      if (d.text && !d.isError) parts.push(d.text);
      if (p.text && !p.isError) parts.push(p.text);
      if (s.text && !s.isError) parts.push(s.text);
      return this.ok(parts.join('\n\n') || 'No resources found.');
    }

    switch (type) {
      case 'namespaces': {
        const rows = [['NAME', 'STATUS', 'AGE']];
        this.state.namespaces.forEach((n) => rows.push([n.name, 'Active', fmtAge(n.createdAt)]));
        return this.ok(table(rows));
      }
      case 'nodes': {
        let ns2 = this.state.nodes;
        if (explicitName) ns2 = ns2.filter((n) => n.name === explicitName);
        if (ns2.length === 0) return this.ok('No resources found.');
        const wide = (flags['o'] ?? flags['output']) === 'wide';
        const showLabels = flags['show-labels'] === true;
        const header = ['NAME', 'STATUS', 'ROLES', 'AGE', 'VERSION'];
        if (wide) header.push('INTERNAL-IP', 'OS-IMAGE', 'KERNEL-VERSION', 'CONTAINER-RUNTIME');
        if (showLabels) header.push('LABELS');
        const rows = [header];
        ns2.forEach((n) => {
          const roles = n.role === 'control-plane' ? 'control-plane' : '<none>';
          const status = n.schedulable ? n.status : `${n.status},SchedulingDisabled`;
          const row = [n.name, status, roles, fmtAge(n.createdAt), n.version];
          if (wide) row.push(n.internalIP, 'Ubuntu 22.04.5 LTS', '5.15.0-1078-aws', 'containerd://1.7.24');
          if (showLabels) {
            const base = `beta.kubernetes.io/arch=amd64,kubernetes.io/hostname=${n.name},kubernetes.io/os=linux`;
            row.push(n.role === 'control-plane' ? `${base},node-role.kubernetes.io/control-plane=` : base);
          }
          rows.push(row);
        });
        return this.ok(table(rows));
      }
      case 'pods': {
        let pods = this.state.pods;
        if (!allNs) pods = pods.filter((p) => p.namespace === ns);
        if (explicitName) pods = pods.filter((p) => p.name === explicitName);
        if (pods.length === 0) return this.ok(allNs ? 'No resources found.' : `No resources found in ${ns} namespace.`);
        const wide = (flags['o'] ?? flags['output']) === 'wide';
        const cols = allNs ? ['NAMESPACE', 'NAME', 'READY', 'STATUS', 'RESTARTS', 'AGE'] : ['NAME', 'READY', 'STATUS', 'RESTARTS', 'AGE'];
        if (wide) cols.push('IP', 'NODE', 'NOMINATED NODE', 'READINESS GATES');
        const rows = [cols];
        pods.forEach((p) => {
          const ready = p.status === 'Running' ? `${p.containers.length}/${p.containers.length}` : `0/${p.containers.length}`;
          const base = [`${p.name}`, ready, p.status, String(p.restarts), fmtAge(p.createdAt)];
          const row = allNs ? [p.namespace, ...base] : base;
          if (wide) row.push(p.ip, p.node, '<none>', '<none>');
          rows.push(row);
        });
        return this.ok(table(rows));
      }
      case 'deployments': {
        let deps = this.state.deployments;
        if (!allNs) deps = deps.filter((d) => d.namespace === ns);
        if (explicitName) deps = deps.filter((d) => d.name === explicitName);
        if (deps.length === 0) return this.ok(allNs ? 'No resources found.' : `No resources found in ${ns} namespace.`);
        const header = allNs ? ['NAMESPACE', 'NAME', 'READY', 'UP-TO-DATE', 'AVAILABLE', 'AGE'] : ['NAME', 'READY', 'UP-TO-DATE', 'AVAILABLE', 'AGE'];
        const rows = [header];
        deps.forEach((d) => {
          const base = [d.name, `${d.replicas}/${d.replicas}`, String(d.replicas), String(d.replicas), fmtAge(d.createdAt)];
          rows.push(allNs ? [d.namespace, ...base] : base);
        });
        return this.ok(table(rows));
      }
      case 'statefulsets': {
        let items = this.state.statefulsets;
        if (!allNs) items = items.filter((x) => x.namespace === ns);
        if (explicitName) items = items.filter((x) => x.name === explicitName);
        if (items.length === 0) return this.ok(allNs ? 'No resources found.' : `No resources found in ${ns} namespace.`);
        const header = allNs ? ['NAMESPACE', 'NAME', 'READY', 'AGE'] : ['NAME', 'READY', 'AGE'];
        const rows = [header];
        items.forEach((x) => {
          const base = [x.name, `${x.replicas}/${x.replicas}`, fmtAge(x.createdAt)];
          rows.push(allNs ? [x.namespace, ...base] : base);
        });
        return this.ok(table(rows));
      }
      case 'daemonsets': {
        let items = this.state.daemonsets;
        if (!allNs) items = items.filter((x) => x.namespace === ns);
        if (explicitName) items = items.filter((x) => x.name === explicitName);
        if (items.length === 0) return this.ok(allNs ? 'No resources found.' : `No resources found in ${ns} namespace.`);
        const header = allNs ? ['NAMESPACE', 'NAME', 'DESIRED', 'CURRENT', 'READY', 'UP-TO-DATE', 'AVAILABLE', 'NODE SELECTOR', 'AGE'] : ['NAME', 'DESIRED', 'CURRENT', 'READY', 'UP-TO-DATE', 'AVAILABLE', 'NODE SELECTOR', 'AGE'];
        const rows = [header];
        items.forEach((x) => {
          const n = this.state.pods.filter((p) => p.ownerName === x.name && p.namespace === x.namespace && p.labels['app.kubernetes.io/managed-by'] === 'daemonset-sim').length;
          const base = [x.name, String(n), String(n), String(n), String(n), String(n), '<none>', fmtAge(x.createdAt)];
          rows.push(allNs ? [x.namespace, ...base] : base);
        });
        return this.ok(table(rows));
      }
      case 'horizontalpodautoscalers': {
        let items = this.state.horizontalpodautoscalers;
        if (!allNs) items = items.filter((x) => x.namespace === ns);
        if (explicitName) items = items.filter((x) => x.name === explicitName);
        if (items.length === 0) return this.ok(allNs ? 'No resources found.' : `No resources found in ${ns} namespace.`);
        const header = allNs ? ['NAMESPACE', 'NAME', 'REFERENCE', 'TARGETS', 'MINPODS', 'MAXPODS', 'AGE'] : ['NAME', 'REFERENCE', 'TARGETS', 'MINPODS', 'MAXPODS', 'AGE'];
        const rows = [header];
        items.forEach((x) => {
          const target = x.cpuUtilization != null ? `cpu: <unknown>/${x.cpuUtilization}%` : '<none>';
          const base = [x.name, `${x.targetKind}/${x.targetName}`, target, String(x.minReplicas), String(x.maxReplicas), fmtAge(x.createdAt)];
          rows.push(allNs ? [x.namespace, ...base] : base);
        });
        return this.ok(table(rows));
      }
      case 'services': {
        let svcs = this.state.services;
        if (!allNs) svcs = svcs.filter((s) => s.namespace === ns);
        if (explicitName) svcs = svcs.filter((s) => s.name === explicitName);
        if (svcs.length === 0) return this.ok(allNs ? 'No resources found.' : `No resources found in ${ns} namespace.`);
        const header = allNs ? ['NAMESPACE', 'NAME', 'TYPE', 'CLUSTER-IP', 'PORT(S)', 'AGE'] : ['NAME', 'TYPE', 'CLUSTER-IP', 'PORT(S)', 'AGE'];
        const rows = [header];
        svcs.forEach((s) => {
          const base = [s.name, s.type, s.clusterIP, `${s.port}/TCP`, fmtAge(s.createdAt)];
          rows.push(allNs ? [s.namespace, ...base] : base);
        });
        return this.ok(table(rows));
      }
      case 'configmaps': {
        let cms = this.state.configmaps;
        if (!allNs) cms = cms.filter((c) => c.namespace === ns);
        if (explicitName) cms = cms.filter((c) => c.name === explicitName);
        if (cms.length === 0) return this.ok(allNs ? 'No resources found.' : `No resources found in ${ns} namespace.`);
        const rows = [['NAME', 'DATA', 'AGE']];
        cms.forEach((c) => rows.push([c.name, String(Object.keys(c.data).length), fmtAge(c.createdAt)]));
        return this.ok(table(rows));
      }
      case 'secrets': {
        let secs = this.state.secrets;
        if (!allNs) secs = secs.filter((c) => c.namespace === ns);
        if (explicitName) secs = secs.filter((c) => c.name === explicitName);
        if (secs.length === 0) return this.ok(allNs ? 'No resources found.' : `No resources found in ${ns} namespace.`);
        const rows = [['NAME', 'TYPE', 'DATA', 'AGE']];
        secs.forEach((c) => rows.push([c.name, c.type, String(Object.keys(c.data).length), fmtAge(c.createdAt)]));
        return this.ok(table(rows));
      }
      case 'serviceaccounts': {
        let sas = this.state.serviceaccounts;
        if (!allNs) sas = sas.filter((c) => c.namespace === ns);
        if (explicitName) sas = sas.filter((c) => c.name === explicitName);
        // Le SA "default" existe toujours dans un namespace réel
        if (!explicitName && !sas.some((s) => s.name === 'default')) {
          sas = [{ name: 'default', namespace: ns, createdAt: Date.now() }, ...sas];
        }
        if (sas.length === 0) return this.ok(allNs ? 'No resources found.' : `No resources found in ${ns} namespace.`);
        const rows = [['NAME', 'AGE']];
        sas.forEach((c) => rows.push([c.name, fmtAge(c.createdAt)]));
        return this.ok(table(rows));
      }
      case 'roles': {
        let rs = this.state.roles;
        if (!allNs) rs = rs.filter((r) => r.namespace === ns);
        if (explicitName) rs = rs.filter((r) => r.name === explicitName);
        if (rs.length === 0) return this.ok(allNs ? 'No resources found.' : `No resources found in ${ns} namespace.`);
        const rows = [['NAME', 'CREATED AT']];
        rs.forEach((r) => rows.push([r.name, fmtAge(r.createdAt)]));
        return this.ok(table(rows));
      }
      case 'clusterroles': {
        let rs = this.state.clusterroles;
        if (explicitName) rs = rs.filter((r) => r.name === explicitName);
        if (rs.length === 0) return this.ok('No resources found.');
        const rows = [['NAME', 'CREATED AT']];
        rs.forEach((r) => rows.push([r.name, fmtAge(r.createdAt)]));
        return this.ok(table(rows));
      }
      case 'rolebindings': {
        let bs = this.state.rolebindings;
        if (!allNs) bs = bs.filter((b) => b.namespace === ns);
        if (explicitName) bs = bs.filter((b) => b.name === explicitName);
        if (bs.length === 0) return this.ok(allNs ? 'No resources found.' : `No resources found in ${ns} namespace.`);
        const rows = [['NAME', 'ROLE', 'AGE']];
        bs.forEach((b) => rows.push([b.name, `${b.roleKind}/${b.roleName}`, fmtAge(b.createdAt)]));
        return this.ok(table(rows));
      }
      case 'clusterrolebindings': {
        let bs = this.state.clusterrolebindings;
        if (explicitName) bs = bs.filter((b) => b.name === explicitName);
        if (bs.length === 0) return this.ok('No resources found.');
        const rows = [['NAME', 'ROLE', 'AGE']];
        bs.forEach((b) => rows.push([b.name, `ClusterRole/${b.roleName}`, fmtAge(b.createdAt)]));
        return this.ok(table(rows));
      }
      case 'networkpolicies': {
        let ps = this.state.networkpolicies;
        if (!allNs) ps = ps.filter((p) => p.namespace === ns);
        if (explicitName) ps = ps.filter((p) => p.name === explicitName);
        if (ps.length === 0) return this.ok(allNs ? 'No resources found.' : `No resources found in ${ns} namespace.`);
        const rows = [['NAME', 'POD-SELECTOR', 'AGE']];
        ps.forEach((p) => {
          const sel = Object.entries(p.podSelector).map(([k, v]) => `${k}=${v}`).join(',') || '<none>';
          rows.push([p.name, sel, fmtAge(p.createdAt)]);
        });
        return this.ok(table(rows));
      }
      case 'storageclasses': {
        // Cluster-scoped : ignore -n.
        let scs = this.state.storageclasses;
        if (explicitName) scs = scs.filter((s) => s.name === explicitName);
        if (scs.length === 0) return this.ok('No resources found.');
        const rows = [['NAME', 'PROVISIONER', 'RECLAIMPOLICY', 'VOLUMEBINDINGMODE', 'AGE']];
        scs.forEach((s) => rows.push([
          s.isDefault ? `${s.name} (default)` : s.name,
          s.provisioner, s.reclaimPolicy, s.volumeBindingMode, fmtAge(s.createdAt),
        ]));
        return this.ok(table(rows));
      }
      case 'persistentvolumes': {
        let pvs = this.state.persistentvolumes;
        if (explicitName) pvs = pvs.filter((p) => p.name === explicitName);
        if (pvs.length === 0) return this.ok('No resources found.');
        const rows = [['NAME', 'CAPACITY', 'ACCESS MODES', 'RECLAIM POLICY', 'STATUS', 'CLAIM', 'STORAGECLASS', 'AGE']];
        pvs.forEach((p) => rows.push([
          p.name, p.capacity, this.shortAccessModes(p.accessModes), p.reclaimPolicy,
          p.status, p.claimRef || '', p.storageClassName || '', fmtAge(p.createdAt),
        ]));
        return this.ok(table(rows));
      }
      case 'persistentvolumeclaims': {
        let pvcs = this.state.persistentvolumeclaims;
        if (!allNs) pvcs = pvcs.filter((p) => p.namespace === ns);
        if (explicitName) pvcs = pvcs.filter((p) => p.name === explicitName);
        if (pvcs.length === 0) return this.ok(allNs ? 'No resources found.' : `No resources found in ${ns} namespace.`);
        const rows = [['NAME', 'STATUS', 'VOLUME', 'CAPACITY', 'ACCESS MODES', 'STORAGECLASS', 'AGE']];
        pvcs.forEach((p) => rows.push([
          p.name, p.status, p.volumeName || '', p.status === 'Bound' ? p.storageRequest : '',
          this.shortAccessModes(p.accessModes), p.storageClassName || '', fmtAge(p.createdAt),
        ]));
        return this.ok(table(rows));
      }
      default:
        return this.err(`error: the server doesn't have a resource type "${type}".`);
    }
  }

  private cmdCreate(args: string[]): ExecResult {
    // create -f <file> : même logique que apply -f (sans mise à jour idempotente).
    if (args.includes('-f') || args.includes('--filename') || args.some((a) => a.startsWith('--filename='))) {
      return this.cmdApplyFile(args, 'create');
    }
    const sub = args[0];
    const { positional, flags } = parseArgs(args.slice(1));
    const ns = this.resolveNs(flags);

    if (sub === 'deployment' || sub === 'deploy') {
      const name = positional[0];
      if (!name) return this.err('error: NAME is required for deployment.');
      const image = flags['image'] as string;
      if (!image) return this.err('error: --image is required.');
      const replicas = flags['replicas'] ? parseInt(flags['replicas'] as string, 10) : 1;
      // --dry-run=client -o yaml : génère le manifeste sans rien créer.
      if (this.isDryRun(flags) && this.wantsYaml(flags)) {
        return this.ok(this.deploymentYaml(name, image, replicas));
      }
      if (this.state.deployments.some((d) => d.name === name && d.namespace === ns)) {
        return this.err(`error: deployments.apps "${name}" already exists`);
      }
      this.ensureNs(ns);
      const dep: DeploymentObj = {
        name, namespace: ns, labels: { app: name }, selector: { app: name },
        replicas, containerName: name, image, revision: 1,
        history: [{ revision: 1, image }], createdAt: Date.now(),
      };
      this.state.deployments.push(dep);
      this.reconcilePods(dep);
      return this.ok(`deployment.apps/${name} created`);
    }

    if (sub === 'namespace' || sub === 'ns') {
      const name = positional[0];
      if (!name) return this.err('error: NAME is required.');
      if (this.state.namespaces.some((n) => n.name === name)) return this.err(`error: namespaces "${name}" already exists`);
      this.state.namespaces.push({ name, createdAt: Date.now() });
      return this.ok(`namespace/${name} created`);
    }

    if (sub === 'configmap' || sub === 'cm') {
      const name = positional[0];
      if (!name) return this.err('error: NAME is required.');
      const data: Record<string, string> = {};
      const lit = flags['from-literal'];
      if (typeof lit === 'string') {
        const eq = lit.indexOf('=');
        if (eq >= 0) data[lit.slice(0, eq)] = lit.slice(eq + 1);
      }
      this.ensureNs(ns);
      this.state.configmaps.push({ name, namespace: ns, data, createdAt: Date.now() });
      return this.ok(`configmap/${name} created`);
    }

    if (sub === 'secret') {
      // kubectl create secret generic <nom> --from-literal=k=v
      const kind = positional[0];
      const name = positional[1];
      if (kind !== 'generic') return this.err("error: only 'generic' secrets are supported in this simulator.");
      if (!name) return this.err('error: NAME is required.');
      const data: Record<string, string> = {};
      const lit = flags['from-literal'];
      if (typeof lit === 'string') {
        const eq = lit.indexOf('=');
        if (eq >= 0) data[lit.slice(0, eq)] = lit.slice(eq + 1);
      }
      this.ensureNs(ns);
      this.state.secrets.push({ name, namespace: ns, type: 'Opaque', data, createdAt: Date.now() });
      return this.ok(`secret/${name} created`);
    }

    if (sub === 'serviceaccount' || sub === 'sa') {
      const name = positional[0];
      if (!name) return this.err('error: NAME is required.');
      if (this.state.serviceaccounts.some((s) => s.name === name && s.namespace === ns)) {
        return this.err(`error: serviceaccounts "${name}" already exists`);
      }
      this.ensureNs(ns);
      this.state.serviceaccounts.push({ name, namespace: ns, createdAt: Date.now() });
      return this.ok(`serviceaccount/${name} created`);
    }

    if (sub === 'role' || sub === 'clusterrole') {
      const name = positional[0];
      if (!name) return this.err('error: NAME is required.');
      const verbs = collectFlag(args.slice(1), 'verb');
      const resources = collectFlag(args.slice(1), 'resource');
      if (verbs.length === 0 || resources.length === 0) {
        return this.err('error: at least one --verb and one --resource must be specified.');
      }
      const isCluster = sub === 'clusterrole';
      const role: RoleObj = { name, namespace: isCluster ? '' : ns, cluster: isCluster, verbs, resources, createdAt: Date.now() };
      if (isCluster) this.state.clusterroles.push(role);
      else { this.ensureNs(ns); this.state.roles.push(role); }
      return this.ok(`${sub}.rbac.authorization.k8s.io/${name} created`);
    }

    if (sub === 'rolebinding' || sub === 'clusterrolebinding') {
      const name = positional[0];
      if (!name) return this.err('error: NAME is required.');
      const isCluster = sub === 'clusterrolebinding';
      const clusterrole = flags['clusterrole'] as string | undefined;
      const role = flags['role'] as string | undefined;
      if (isCluster && !clusterrole) return this.err('error: --clusterrole is required for clusterrolebinding.');
      if (!isCluster && !role && !clusterrole) return this.err('error: exactly one of --role or --clusterrole must be specified.');
      const subjects = this.parseSubjects(args.slice(1));
      if (subjects.length === 0) return this.err('error: at least one of --user, --group, or --serviceaccount must be specified.');
      const binding: RoleBindingObj = {
        name, namespace: isCluster ? '' : ns, cluster: isCluster,
        roleKind: clusterrole ? 'ClusterRole' : 'Role',
        roleName: (clusterrole || role) as string, subjects, createdAt: Date.now(),
      };
      if (isCluster) this.state.clusterrolebindings.push(binding);
      else { this.ensureNs(ns); this.state.rolebindings.push(binding); }
      return this.ok(`${sub}.rbac.authorization.k8s.io/${name} created`);
    }

    if (sub === 'service' || sub === 'svc') {
      return this.err("Utilise 'kubectl expose deployment <nom> --port=<p>' dans ce simulateur.");
    }

    return this.err(`error: unknown create subcommand "${sub}". Supporté : deployment, namespace, configmap, secret, serviceaccount, role, clusterrole, rolebinding, clusterrolebinding.`);
  }

  /** Sujets d'un (cluster)rolebinding : --serviceaccount=ns:sa, --user, --group. */
  private parseSubjects(args: string[]): RoleBindingSubject[] {
    const subs: RoleBindingSubject[] = [];
    for (const sa of collectFlag(args, 'serviceaccount')) {
      const [nsPart, saName] = sa.includes(':') ? sa.split(':') : ['default', sa];
      subs.push({ kind: 'ServiceAccount', name: saName, namespace: nsPart });
    }
    for (const u of collectFlag(args, 'user')) subs.push({ kind: 'User', name: u });
    for (const g of collectFlag(args, 'group')) subs.push({ kind: 'Group', name: g });
    return subs;
  }

  private cmdRun(args: string[]): ExecResult {
    const { positional, flags } = parseArgs(args);
    const name = positional[0];
    if (!name) return this.err('error: NAME is required for run.');
    const image = flags['image'] as string;
    if (!image) return this.err('error: --image is required.');
    const ns = this.resolveNs(flags);
    // --dry-run=client -o yaml : génère le manifeste du pod sans le créer.
    if (this.isDryRun(flags) && this.wantsYaml(flags)) {
      return this.ok(this.podYaml(name, image));
    }
    if (this.state.pods.some((p) => p.name === name && p.namespace === ns)) {
      return this.err(`error: pods "${name}" already exists`);
    }
    this.ensureNs(ns);
    // --labels="k=v,k2=v2" (comme kubectl run --labels).
    const labels: Record<string, string> = { run: name };
    const lbl = flags['labels'] as string | undefined;
    if (typeof lbl === 'string') {
      for (const kv of lbl.split(',')) {
        const eq = kv.indexOf('=');
        if (eq > 0) labels[kv.slice(0, eq).trim()] = kv.slice(eq + 1).trim();
      }
    }
    this.state.pods.push({
      name, namespace: ns, labels,
      containers: [{ name, image }], status: 'Running', restarts: 0,
      ownerKind: null, ownerName: null,
      node: randWorker(), ip: randIp(), createdAt: Date.now(),
    });
    return this.ok(`pod/${name} created`);
  }

  private cmdScale(args: string[]): ExecResult {
    const { positional, flags } = parseArgs(args);
    if (flags['replicas'] === undefined) return this.err('error: --replicas is required.');
    const replicas = parseInt(flags['replicas'] as string, 10);
    if (Number.isNaN(replicas) || replicas < 0) return this.err('error: --replicas must be a non-negative integer.');
    const ns = this.resolveNs(flags);
    const { type, name } = this.splitTypeName(positional[0] || '');
    const target = name || positional[1];
    if (!target) return this.err('error: usage: kubectl scale deployment/<name> --replicas=<n>');
    if (type === 'statefulsets') {
      const sts = this.state.statefulsets.find((x) => x.name === target && x.namespace === ns);
      if (!sts) return this.err(`Error from server (NotFound): statefulsets.apps "${target}" not found`);
      sts.replicas = replicas;
      this.reconcileStsPods(sts);
      return this.ok(`statefulset.apps/${target} scaled`);
    }
    if (type !== 'deployments') return this.err('error: usage: kubectl scale deployment/<name> --replicas=<n>');
    const dep = this.state.deployments.find((d) => d.name === target && d.namespace === ns);
    if (!dep) return this.err(`Error from server (NotFound): deployments.apps "${target}" not found`);
    dep.replicas = replicas;
    this.reconcilePods(dep);
    return this.ok(`deployment.apps/${target} scaled`);
  }

  // `kubectl logs <pod> [-c <container>]` : sortie simulée (le moteur ne capture
  // pas les flux réels des conteneurs) — évite une erreur sur les commandes de
  // diagnostic courantes.
  private cmdLogs(args: string[]): ExecResult {
    const { positional, flags } = parseArgs(args);
    const name = positional[0];
    if (!name) return this.err('error: you must specify the name of a resource to get logs from.');
    const ns = this.resolveNs(flags);
    const p = this.state.pods.find((x) => x.name === name && x.namespace === ns)
      || this.state.pods.find((x) => x.name === name);
    if (!p) return this.err(`Error from server (NotFound): pods "${name}" not found`);
    return this.ok(`# (logs simulés) flux du pod ${name} — le simulateur ne capture pas la sortie réelle des conteneurs.`);
  }

  private cmdAutoscale(args: string[]): ExecResult {
    const { positional, flags } = parseArgs(args);
    const { type, name } = this.splitTypeName(positional[0] || '');
    const target = name || positional[1];
    if (type !== 'deployments' || !target) return this.err('error: usage: kubectl autoscale deployment <name> --min=<n> --max=<n> --cpu-percent=<n>');
    const ns = this.resolveNs(flags);
    const dep = this.state.deployments.find((d) => d.name === target && d.namespace === ns);
    if (!dep) return this.err(`Error from server (NotFound): deployments.apps "${target}" not found`);
    if (flags['max'] === undefined) return this.err('error: --max is required.');
    const hpaName = (flags['name'] as string) || target;
    const hpa: HpaObj = {
      name: hpaName, namespace: ns, targetKind: 'Deployment', targetName: target,
      minReplicas: flags['min'] !== undefined ? parseInt(flags['min'] as string, 10) : 1,
      maxReplicas: parseInt(flags['max'] as string, 10),
      cpuUtilization: flags['cpu-percent'] !== undefined ? parseInt(flags['cpu-percent'] as string, 10) : null,
      createdAt: Date.now(),
    };
    const idx = this.state.horizontalpodautoscalers.findIndex((x) => x.name === hpaName && x.namespace === ns);
    if (idx >= 0) this.state.horizontalpodautoscalers[idx] = hpa; else this.state.horizontalpodautoscalers.push(hpa);
    return this.ok(`horizontalpodautoscaler.autoscaling/${hpaName} autoscaled`);
  }

  private cmdDelete(args: string[]): ExecResult {
    const { positional, flags } = parseArgs(args);
    const { type, name } = this.splitTypeName(positional[0] || '');
    const target = name || positional[1];
    if (!target) return this.err('error: resource(s) were provided, but no name was specified.');
    const ns = this.resolveNs(flags);

    if (type === 'deployments') {
      const before = this.state.deployments.length;
      this.state.deployments = this.state.deployments.filter((d) => !(d.name === target && d.namespace === ns));
      if (this.state.deployments.length === before) return this.err(`Error from server (NotFound): deployments.apps "${target}" not found`);
      this.state.pods = this.state.pods.filter((p) => !(p.ownerName === target && p.namespace === ns));
      return this.ok(`deployment.apps "${target}" deleted`);
    }
    if (type === 'pods') {
      const before = this.state.pods.length;
      this.state.pods = this.state.pods.filter((p) => !(p.name === target && p.namespace === ns));
      if (this.state.pods.length === before) return this.err(`Error from server (NotFound): pods "${target}" not found`);
      return this.ok(`pod "${target}" deleted`);
    }
    if (type === 'statefulsets') {
      const before = this.state.statefulsets.length;
      this.state.statefulsets = this.state.statefulsets.filter((x) => !(x.name === target && x.namespace === ns));
      if (this.state.statefulsets.length === before) return this.err(`Error from server (NotFound): statefulsets.apps "${target}" not found`);
      this.state.pods = this.state.pods.filter((p) => !(p.ownerName === target && p.namespace === ns && p.labels['app.kubernetes.io/managed-by'] === 'statefulset-sim'));
      return this.ok(`statefulset.apps "${target}" deleted`);
    }
    if (type === 'daemonsets') {
      const before = this.state.daemonsets.length;
      this.state.daemonsets = this.state.daemonsets.filter((x) => !(x.name === target && x.namespace === ns));
      if (this.state.daemonsets.length === before) return this.err(`Error from server (NotFound): daemonsets.apps "${target}" not found`);
      this.state.pods = this.state.pods.filter((p) => !(p.ownerName === target && p.namespace === ns && p.labels['app.kubernetes.io/managed-by'] === 'daemonset-sim'));
      return this.ok(`daemonset.apps "${target}" deleted`);
    }
    if (type === 'horizontalpodautoscalers') {
      const before = this.state.horizontalpodautoscalers.length;
      this.state.horizontalpodautoscalers = this.state.horizontalpodautoscalers.filter((x) => !(x.name === target && x.namespace === ns));
      if (this.state.horizontalpodautoscalers.length === before) return this.err(`Error from server (NotFound): horizontalpodautoscalers.autoscaling "${target}" not found`);
      return this.ok(`horizontalpodautoscaler.autoscaling "${target}" deleted`);
    }
    if (type === 'services') {
      const before = this.state.services.length;
      this.state.services = this.state.services.filter((s) => !(s.name === target && s.namespace === ns));
      if (this.state.services.length === before) return this.err(`Error from server (NotFound): services "${target}" not found`);
      return this.ok(`service "${target}" deleted`);
    }
    if (type === 'configmaps') {
      this.state.configmaps = this.state.configmaps.filter((c) => !(c.name === target && c.namespace === ns));
      return this.ok(`configmap "${target}" deleted`);
    }
    if (type === 'secrets') {
      this.state.secrets = this.state.secrets.filter((c) => !(c.name === target && c.namespace === ns));
      return this.ok(`secret "${target}" deleted`);
    }
    if (type === 'serviceaccounts') {
      this.state.serviceaccounts = this.state.serviceaccounts.filter((c) => !(c.name === target && c.namespace === ns));
      return this.ok(`serviceaccount "${target}" deleted`);
    }
    if (type === 'roles') {
      this.state.roles = this.state.roles.filter((r) => !(r.name === target && r.namespace === ns));
      return this.ok(`role.rbac.authorization.k8s.io "${target}" deleted`);
    }
    if (type === 'clusterroles') {
      this.state.clusterroles = this.state.clusterroles.filter((r) => r.name !== target);
      return this.ok(`clusterrole.rbac.authorization.k8s.io "${target}" deleted`);
    }
    if (type === 'rolebindings') {
      this.state.rolebindings = this.state.rolebindings.filter((b) => !(b.name === target && b.namespace === ns));
      return this.ok(`rolebinding.rbac.authorization.k8s.io "${target}" deleted`);
    }
    if (type === 'clusterrolebindings') {
      this.state.clusterrolebindings = this.state.clusterrolebindings.filter((b) => b.name !== target);
      return this.ok(`clusterrolebinding.rbac.authorization.k8s.io "${target}" deleted`);
    }
    if (type === 'networkpolicies') {
      this.state.networkpolicies = this.state.networkpolicies.filter((p) => !(p.name === target && p.namespace === ns));
      return this.ok(`networkpolicy.networking.k8s.io "${target}" deleted`);
    }
    if (type === 'storageclasses') {
      const before = this.state.storageclasses.length;
      this.state.storageclasses = this.state.storageclasses.filter((s) => s.name !== target);
      if (this.state.storageclasses.length === before) return this.err(`Error from server (NotFound): storageclasses.storage.k8s.io "${target}" not found`);
      return this.ok(`storageclass.storage.k8s.io "${target}" deleted`);
    }
    if (type === 'persistentvolumes') {
      const before = this.state.persistentvolumes.length;
      this.state.persistentvolumes = this.state.persistentvolumes.filter((p) => p.name !== target);
      if (this.state.persistentvolumes.length === before) return this.err(`Error from server (NotFound): persistentvolumes "${target}" not found`);
      return this.ok(`persistentvolume "${target}" deleted`);
    }
    if (type === 'persistentvolumeclaims') {
      const before = this.state.persistentvolumeclaims.length;
      const removed = this.state.persistentvolumeclaims.find((p) => p.name === target && p.namespace === ns);
      this.state.persistentvolumeclaims = this.state.persistentvolumeclaims.filter((p) => !(p.name === target && p.namespace === ns));
      if (this.state.persistentvolumeclaims.length === before) return this.err(`Error from server (NotFound): persistentvolumeclaims "${target}" not found`);
      // Libère le PV lié (Retain → Released, Delete → supprimé).
      if (removed?.volumeName) {
        const pv = this.state.persistentvolumes.find((p) => p.name === removed.volumeName);
        if (pv) {
          if (pv.reclaimPolicy === 'Delete') {
            this.state.persistentvolumes = this.state.persistentvolumes.filter((p) => p.name !== pv.name);
          } else {
            pv.status = 'Released';
            pv.claimRef = null;
          }
        }
      }
      return this.ok(`persistentvolumeclaim "${target}" deleted`);
    }
    return this.err(`error: unsupported resource type "${type}" for delete.`);
  }

  private cmdExpose(args: string[]): ExecResult {
    const { positional, flags } = parseArgs(args);
    const { type, name } = this.splitTypeName(positional[0] || '');
    const depName = name || positional[1];
    if (type !== 'deployments' || !depName) return this.err('error: usage: kubectl expose deployment <name> --port=<p>');
    const ns = this.resolveNs(flags);
    const dep = this.state.deployments.find((d) => d.name === depName && d.namespace === ns);
    if (!dep) return this.err(`Error from server (NotFound): deployments.apps "${depName}" not found`);
    if (flags['port'] === undefined) return this.err('error: --port is required.');
    const port = parseInt(flags['port'] as string, 10);
    const targetPort = flags['target-port'] ? parseInt(flags['target-port'] as string, 10) : port;
    const svcName = (flags['name'] as string) || depName;
    const type_ = (flags['type'] as string) === 'NodePort' ? 'NodePort' : 'ClusterIP';
    this.state.services.push({
      name: svcName, namespace: ns, type: type_ as 'ClusterIP' | 'NodePort',
      clusterIP: `10.96.${Math.floor(Math.random() * 250)}.${Math.floor(Math.random() * 250)}`,
      port, targetPort, selector: { ...dep.selector }, createdAt: Date.now(),
    });
    return this.ok(`service/${svcName} exposed`);
  }

  private cmdLabel(args: string[]): ExecResult {
    const { positional, flags } = parseArgs(args);
    const { type, name } = this.splitTypeName(positional[0] || '');
    const target = name || positional[1];
    const kv = positional.find((p, i) => i > 0 && p.includes('='));
    if (!target || !kv) return this.err('error: usage: kubectl label <type>/<name> key=value');
    const ns = this.resolveNs(flags);
    const [k, v] = kv.split('=');
    if (type === 'pods') {
      const pod = this.state.pods.find((p) => p.name === target && p.namespace === ns);
      if (!pod) return this.err(`Error from server (NotFound): pods "${target}" not found`);
      pod.labels[k] = v;
      return this.ok(`pod/${target} labeled`);
    }
    if (type === 'deployments') {
      const dep = this.state.deployments.find((d) => d.name === target && d.namespace === ns);
      if (!dep) return this.err(`Error from server (NotFound): deployments.apps "${target}" not found`);
      dep.labels[k] = v;
      return this.ok(`deployment.apps/${target} labeled`);
    }
    return this.err(`error: unsupported resource type "${type}" for label.`);
  }

  private cmdSet(args: string[]): ExecResult {
    if (args[0] !== 'image') return this.err(`error: unknown set subcommand "${args[0]}". Supporté : image.`);
    const { positional, flags } = parseArgs(args.slice(1));
    const { type, name } = this.splitTypeName(positional[0] || '');
    const depName = name || positional[1];
    if (type !== 'deployments' || !depName) return this.err('error: usage: kubectl set image deployment/<name> <container>=<image>');
    const spec = positional.find((p) => p.includes('='));
    if (!spec) return this.err('error: you must specify container=image.');
    const ns = this.resolveNs(flags);
    const dep = this.state.deployments.find((d) => d.name === depName && d.namespace === ns);
    if (!dep) return this.err(`Error from server (NotFound): deployments.apps "${depName}" not found`);
    const newImage = spec.slice(spec.indexOf('=') + 1);
    if (newImage === dep.image) return this.ok(`deployment.apps/${depName} image updated (no change)`);
    dep.image = newImage;
    dep.revision += 1;
    dep.history.push({ revision: dep.revision, image: newImage });
    this.reconcilePods(dep);
    return this.ok(`deployment.apps/${depName} image updated`);
  }

  private cmdRollout(args: string[]): ExecResult {
    const sub = args[0];
    const { positional, flags } = parseArgs(args.slice(1));
    const { type, name } = this.splitTypeName(positional[0] || '');
    const depName = name || positional[1];
    if (type !== 'deployments' || !depName) return this.err('error: usage: kubectl rollout <sub> deployment/<name>');
    const ns = this.resolveNs(flags);
    const dep = this.state.deployments.find((d) => d.name === depName && d.namespace === ns);
    if (!dep) return this.err(`Error from server (NotFound): deployments.apps "${depName}" not found`);

    if (sub === 'undo') {
      if (dep.history.length < 2) return this.err(`error: no rollout history found for deployment.apps/${depName}`);
      const prev = dep.history[dep.history.length - 2];
      dep.image = prev.image;
      dep.revision += 1;
      dep.history.push({ revision: dep.revision, image: prev.image });
      this.reconcilePods(dep);
      return this.ok(`deployment.apps/${depName} rolled back`);
    }
    if (sub === 'status') return this.ok(`deployment "${depName}" successfully rolled out`);
    if (sub === 'history') {
      const rows = [['REVISION', 'CHANGE-CAUSE']];
      dep.history.forEach((h) => rows.push([String(h.revision), `image=${h.image}`]));
      return this.ok(table(rows));
    }
    if (sub === 'restart') {
      this.state.pods.filter((p) => p.ownerName === depName && p.namespace === ns).forEach((p) => { p.createdAt = Date.now(); });
      return this.ok(`deployment.apps/${depName} restarted`);
    }
    return this.err(`error: unknown rollout subcommand "${sub}".`);
  }

  /** Rendu d'une sonde à la façon `kubectl describe`. */
  private describeProbe(p: ProbeSpec): string {
    const timing = `delay=${p.initialDelaySeconds ?? 0}s period=${p.periodSeconds ?? 10}s`;
    if (p.kind === 'httpGet') return `http-get http://:${p.port ?? ''}${p.path ?? '/'} ${timing}`;
    if (p.kind === 'tcpSocket') return `tcp-socket :${p.port ?? ''} ${timing}`;
    return `exec [${(p.command || []).join(' ')}] ${timing}`;
  }

  /** Bloc « Containers: » façon describe, avec command/args/mounts/probes. */
  private describeContainer(c: Container): string {
    const lines = [`  ${c.name}:`, `    Image:  ${c.image}`];
    if (c.command) lines.push(`    Command:\n      ${c.command.join('\n      ')}`);
    if (c.args) lines.push(`    Args:\n      ${c.args.join('\n      ')}`);
    if (c.livenessProbe) lines.push(`    Liveness:   ${this.describeProbe(c.livenessProbe)}`);
    if (c.readinessProbe) lines.push(`    Readiness:  ${this.describeProbe(c.readinessProbe)}`);
    if (c.startupProbe) lines.push(`    Startup:    ${this.describeProbe(c.startupProbe)}`);
    if (c.volumeMounts && c.volumeMounts.length) {
      lines.push('    Mounts:');
      c.volumeMounts.forEach((m) =>
        lines.push(`      ${m.mountPath} from ${m.name}${m.readOnly ? ' (ro)' : ' (rw)'}${m.subPath ? `,path="${m.subPath}"` : ''}`),
      );
    }
    return lines.join('\n');
  }

  /** Bloc « Volumes: » façon describe. */
  private describeVolumes(vols?: PodVolume[]): string {
    if (!vols || vols.length === 0) return 'Volumes:      <none>';
    const lines = ['Volumes:'];
    for (const v of vols) {
      lines.push(`  ${v.name}:`);
      if (v.kind === 'persistentVolumeClaim') {
        lines.push('    Type:       PersistentVolumeClaim (a reference to a PVC in the same namespace)');
        lines.push(`    ClaimName:  ${v.source || ''}`);
      } else if (v.kind === 'configMap') {
        lines.push('    Type:       ConfigMap (a volume populated by a ConfigMap)');
        lines.push(`    Name:       ${v.source || ''}`);
      } else if (v.kind === 'secret') {
        lines.push('    Type:        Secret (a volume populated by a Secret)');
        lines.push(`    SecretName:  ${v.source || ''}`);
      } else if (v.kind === 'emptyDir') {
        lines.push('    Type:       EmptyDir (a temporary directory that shares a pod lifetime)');
      } else if (v.kind === 'hostPath') {
        lines.push('    Type:       HostPath (bare host directory volume)');
        lines.push(`    Path:       ${v.source || ''}`);
      } else {
        lines.push('    Type:       (autre)');
      }
    }
    return lines.join('\n');
  }

  private cmdDescribe(args: string[]): ExecResult {
    const { positional, flags } = parseArgs(args);
    const { type, name } = this.splitTypeName(positional[0] || '');
    const target = name || positional[1];
    if (!target) return this.err('error: a resource name is required.');
    const ns = this.resolveNs(flags);
    if (type === 'nodes') {
      const n = this.state.nodes.find((x) => x.name === target);
      if (!n) return this.err(`Error from server (NotFound): nodes "${target}" not found`);
      const podsOn = this.state.pods.filter((p) => p.node === n.name).length;
      return this.ok(
        `Name:               ${n.name}\n` +
        `Roles:              ${n.role === 'control-plane' ? 'control-plane' : '<none>'}\n` +
        `Labels:             kubernetes.io/hostname=${n.name},kubernetes.io/os=linux${n.role === 'control-plane' ? ',node-role.kubernetes.io/control-plane=' : ''}\n` +
        `Taints:             ${n.taints.length ? n.taints.join('\n                    ') : '<none>'}\n` +
        `Unschedulable:      ${n.schedulable ? 'false' : 'true'}\n` +
        `Addresses:\n  InternalIP:  ${n.internalIP}\n  Hostname:    ${n.name}\n` +
        `System Info:\n  Kernel Version:             5.15.0-1078-aws\n  OS Image:                   Ubuntu 22.04.5 LTS\n  Container Runtime Version:  containerd://1.7.24\n  Kubelet Version:            ${n.version}\n` +
        `Non-terminated Pods:  (${podsOn} in total)`,
      );
    }
    if (type === 'deployments') {
      const dep = this.state.deployments.find((d) => d.name === target && d.namespace === ns);
      if (!dep) return this.err(`Error from server (NotFound): deployments.apps "${target}" not found`);
      return this.ok(
        `Name:                   ${dep.name}\n` +
        `Namespace:              ${dep.namespace}\n` +
        `Selector:               ${Object.entries(dep.selector).map(([k, v]) => `${k}=${v}`).join(',')}\n` +
        `Replicas:               ${dep.replicas} desired | ${dep.replicas} updated | ${dep.replicas} total | ${dep.replicas} available\n` +
        `Labels:                 ${Object.entries(dep.labels).map(([k, v]) => `${k}=${v}`).join(',')}\n` +
        `Revision:               ${dep.revision}\n` +
        `Pod Template:\n  Containers:\n` +
        (dep.templateContainers && dep.templateContainers.length
          ? dep.templateContainers.map((c) => this.describeContainer(c)).join('\n')
          : `  ${dep.containerName}:\n    Image:  ${dep.image}`) +
        `\n  ${this.describeVolumes(dep.volumes)}`,
      );
    }
    if (type === 'pods') {
      const pod = this.state.pods.find((p) => p.name === target && p.namespace === ns);
      if (!pod) return this.err(`Error from server (NotFound): pods "${target}" not found`);
      return this.ok(
        `Name:         ${pod.name}\n` +
        `Namespace:    ${pod.namespace}\n` +
        `Node:         ${pod.node}\n` +
        `Status:       ${pod.status}\n` +
        `IP:           ${pod.ip}\n` +
        `Labels:       ${Object.entries(pod.labels).map(([k, v]) => `${k}=${v}`).join(',')}\n` +
        `Containers:\n${pod.containers.map((c) => this.describeContainer(c)).join('\n')}\n` +
        this.describeVolumes(pod.volumes),
      );
    }
    if (type === 'persistentvolumeclaims') {
      const pvc = this.state.persistentvolumeclaims.find((p) => p.name === target && p.namespace === ns);
      if (!pvc) return this.err(`Error from server (NotFound): persistentvolumeclaims "${target}" not found`);
      return this.ok(
        `Name:          ${pvc.name}\n` +
        `Namespace:     ${pvc.namespace}\n` +
        `StorageClass:  ${pvc.storageClassName || ''}\n` +
        `Status:        ${pvc.status}\n` +
        `Volume:        ${pvc.volumeName || ''}\n` +
        `Capacity:      ${pvc.status === 'Bound' ? pvc.storageRequest : ''}\n` +
        `Access Modes:  ${pvc.status === 'Bound' ? this.shortAccessModes(pvc.accessModes) : ''}\n` +
        `Requested:     ${pvc.storageRequest}`,
      );
    }
    if (type === 'persistentvolumes') {
      const pv = this.state.persistentvolumes.find((p) => p.name === target);
      if (!pv) return this.err(`Error from server (NotFound): persistentvolumes "${target}" not found`);
      return this.ok(
        `Name:            ${pv.name}\n` +
        `StorageClass:    ${pv.storageClassName || ''}\n` +
        `Status:          ${pv.status}\n` +
        `Claim:           ${pv.claimRef || ''}\n` +
        `Reclaim Policy:  ${pv.reclaimPolicy}\n` +
        `Access Modes:    ${this.shortAccessModes(pv.accessModes)}\n` +
        `Capacity:        ${pv.capacity}\n` +
        `Source:          HostPath (${pv.hostPath || ''})`,
      );
    }
    if (type === 'storageclasses') {
      const sc = this.state.storageclasses.find((s) => s.name === target);
      if (!sc) return this.err(`Error from server (NotFound): storageclasses.storage.k8s.io "${target}" not found`);
      return this.ok(
        `Name:                  ${sc.name}\n` +
        `IsDefaultClass:        ${sc.isDefault ? 'Yes' : 'No'}\n` +
        `Provisioner:           ${sc.provisioner}\n` +
        `ReclaimPolicy:         ${sc.reclaimPolicy}\n` +
        `VolumeBindingMode:     ${sc.volumeBindingMode}\n` +
        `Parameters:            ${Object.entries(sc.parameters).map(([k, v]) => `${k}=${v}`).join(',') || '<none>'}`,
      );
    }
    if (type === 'statefulsets') {
      const x = this.state.statefulsets.find((y) => y.name === target && y.namespace === ns);
      if (!x) return this.err(`Error from server (NotFound): statefulsets.apps "${target}" not found`);
      return this.ok(
        `Name:               ${x.name}\n` +
        `Namespace:          ${x.namespace}\n` +
        `Selector:           ${Object.entries(x.selector).map(([k, v]) => `${k}=${v}`).join(',')}\n` +
        `Replicas:           ${x.replicas} desired | ${x.replicas} total\n` +
        `Pod Template:\n  Containers:\n   ${x.containerName}:\n    Image:  ${x.image}`,
      );
    }
    if (type === 'daemonsets') {
      const x = this.state.daemonsets.find((y) => y.name === target && y.namespace === ns);
      if (!x) return this.err(`Error from server (NotFound): daemonsets.apps "${target}" not found`);
      const n = this.state.pods.filter((p) => p.ownerName === x.name && p.namespace === x.namespace && p.labels['app.kubernetes.io/managed-by'] === 'daemonset-sim').length;
      return this.ok(
        `Name:           ${x.name}\n` +
        `Namespace:      ${x.namespace}\n` +
        `Selector:       ${Object.entries(x.selector).map(([k, v]) => `${k}=${v}`).join(',')}\n` +
        `Labels:         ${Object.entries(x.labels).map(([k, v]) => `${k}=${v}`).join(',')}\n` +
        `Desired/Current: ${n}/${n}\n` +
        `Tolerations:    ${(x.tolerations || []).map((t) => `${t.key || ''}:${t.effect || ''}`).join(', ') || '<none>'}\n` +
        `Requests:       ${Object.entries(x.requests || {}).map(([k, v]) => `${k}=${v}`).join(',') || '<none>'}`,
      );
    }
    if (type === 'horizontalpodautoscalers') {
      const x = this.state.horizontalpodautoscalers.find((y) => y.name === target && y.namespace === ns);
      if (!x) return this.err(`Error from server (NotFound): horizontalpodautoscalers.autoscaling "${target}" not found`);
      return this.ok(
        `Name:                  ${x.name}\n` +
        `Namespace:             ${x.namespace}\n` +
        `Reference:             ${x.targetKind}/${x.targetName}\n` +
        `Min replicas:          ${x.minReplicas}\n` +
        `Max replicas:          ${x.maxReplicas}\n` +
        `Target CPU:            ${x.cpuUtilization != null ? x.cpuUtilization + '%' : '<none>'}`,
      );
    }
    if (type === 'networkpolicies') {
      const x = this.state.networkpolicies.find((y) => y.name === target && y.namespace === ns);
      if (!x) return this.err(`Error from server (NotFound): networkpolicies.networking.k8s.io "${target}" not found`);
      const fmtSel = (sel: Record<string, string>) => Object.entries(sel).map(([k, v]) => `${k}=${v}`).join(',') || '<none> (all pods)';
      const fmtRules = (rules: NetPolRule[]) =>
        rules.length === 0
          ? '    <none>'
          : rules.map((r) => {
              const peers = r.podSelectors.map((ps) => Object.entries(ps).map(([k, v]) => `${k}=${v}`).join(',')).join(' | ') || '<any>';
              const ports = r.ports.map((p) => `${p.protocol}/${p.port ?? '*'}`).join(', ') || '<any>';
              return `    - Peers: ${peers}\n      Ports: ${ports}`;
            }).join('\n');
      return this.ok(
        `Name:         ${x.name}\n` +
        `Namespace:    ${x.namespace}\n` +
        `PodSelector:  ${fmtSel(x.podSelector)}\n` +
        `PolicyTypes:  ${x.policyTypes.join(', ') || '<none>'}\n` +
        `Allowing ingress traffic:\n${fmtRules(x.ingress)}\n` +
        `Allowing egress traffic:\n${fmtRules(x.egress)}`,
      );
    }
    return this.err(`error: describe non supporté pour "${type}" dans ce simulateur.`);
  }

  private helpText(): string {
    return [
      'Simulateur kubectl (côté client) — commandes supportées :',
      '',
      '  kubectl get pods|deployments|services|configmaps|namespaces|all [-n ns] [-A]',
      '  kubectl create deployment <nom> --image=<img> [--replicas=<n>]',
      '  kubectl create namespace <nom>',
      '  kubectl create configmap <nom> --from-literal=<k>=<v>',
      '  kubectl create secret generic <nom> --from-literal=<k>=<v>',
      '  kubectl create serviceaccount <nom>',
      '  kubectl create role <nom> --verb=get,list --resource=pods',
      '  kubectl create rolebinding <nom> --role=<r> --serviceaccount=<ns>:<sa>',
      '  kubectl create clusterrole <nom> --verb=... --resource=...',
      '  kubectl create clusterrolebinding <nom> --clusterrole=<cr> --serviceaccount=<ns>:<sa>',
      '  kubectl get roles|rolebindings|clusterroles|networkpolicies [-n ns]',
      '  kubectl get sc|pv|pvc [-n ns]          (storageclasses, PV, PVC)',
      '  kubectl get sts|ds|hpa [-n ns]         (statefulsets, daemonsets, HPA)',
      '  kubectl apply -f <fichier.yaml>        (Pod, Deployment, Service, ConfigMap,',
      '                                          Secret, Role/RoleBinding, NetworkPolicy,',
      '                                          StorageClass, PersistentVolume(Claim)…)',
      '                                          → supporte command/args, volumes,',
      '                                            volumeMounts, liveness/readiness/startupProbe',
      '  kubectl create deployment <nom> --image=<img> --dry-run=client -o yaml',
      '  kubectl get <type>/<nom> -o yaml',
      '  kubectl run <nom> --image=<img> [--labels="k=v,k2=v2"]',
      '  kubectl logs <pod> [-c <conteneur>]   (sortie simulée)',
      '  kubectl scale deployment/<nom>|statefulset/<nom> --replicas=<n>',
      '  kubectl autoscale deployment <nom> --min=<n> --max=<n> --cpu-percent=<n>',
      '  kubectl expose deployment <nom> --port=<p> [--target-port=<t>] [--name=<svc>]',
      '  kubectl set image deployment/<nom> <container>=<img>',
      '  kubectl rollout undo|status|history|restart deployment/<nom>',
      '  kubectl label pod/<nom> <k>=<v>',
      '  kubectl delete <type>/<nom>',
      '  kubectl describe deployment|pod|pvc|pv|sc|sts|ds|hpa|netpol <nom>',
      '  kubectl explain <ressource>          (pod, deployment, service, secret…)',
      '  kubectl <verbe> --help',
      '',
      '  kubectl get nodes [-o wide] [--show-labels] · kubectl describe node <nom>',
      '',
      "Outils d'administration niveau-nœud (comme un vrai serveur) :",
      '  crictl ps|pods|images|inspect|logs|rm|version   (runtime conteneurs)',
      '  kubeadm version|token|certs check-expiration|certs renew|upgrade|join',
      '  openssl x509 -in <cert> -noout -text|-enddate|-issuer|-subject',
      '  systemctl|service <unit> status|start|stop|restart   (ex. kubelet)',
      '  etcdctl version|member list|endpoint health|snapshot save   ·  etcdutl snapshot restore',
      '  sudo [-i] <cmd>   ·   ssh <nœud>   ·   exit',
      '',
      'Commandes Linux de base :',
      '  pwd  ls [-l]  cd  cat  echo [> fichier]  mkdir [-p]  touch  rm [-r]',
      '  cp  mv  grep [-i] <motif> <fichier>  head/tail [-n N]  wc [-l]  whoami  env',
      '  vim/vi <fichier>   — éditeur modal (i insérer, Échap, :wq sauver & quitter)',
      '  nano <fichier>     — éditeur simple (Ctrl+O écrire, Ctrl+X quitter)',
      '  Tab                — complétion (commandes, ressources, namespaces, fichiers)',
      '',
      "  clear   — nettoie l'écran",
      '  help    — affiche cette aide',
      '',
      'Astuce : abrège avec po, deploy, svc, cm, ns. Flèches ↑/↓ = historique.',
    ].join('\n');
  }
}
